<template>
  <div class="spawn-editor animated fadeIn">
    <div class="spawn-layout">
      <aside class="spawn-map-panel">
        <div class="spawn-panel-header">
          <div>
            <div class="spawn-panel-title">Maps</div>
            <small class="text-muted">{{ sidebarSummary }}</small>
          </div>
          <b-button size="sm" variant="outline-info" :disabled="loading" @click="refreshSpawns">
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
          </b-button>
        </div>

        <div class="spawn-workflow-tabs">
          <button
            v-for="view in visibleWorkflowViewOptions"
            :key="'spawn-workflow-view-' + view.value"
            type="button"
            class="spawn-workflow-entry"
            :class="{ active: workflowView === view.value }"
            @click="setWorkflowView(view.value)"
          >
            <span>{{ workflowViewOptionText(view) }}</span>
          </button>
          <b-button
            v-if="workflowView === 'wip' && !editMode"
            size="sm"
            variant="success"
            class="spawn-workflow-action"
            @click="openNewMapModal"
          >
            <i class="fa fa-plus mr-1"></i>New Map
          </b-button>
          <b-button
            v-if="workflowStackAvailable && !editMode"
            size="sm"
            variant="warning"
            class="spawn-workflow-action"
            @click="workflowStackedMaps = !workflowStackedMaps"
          >
            <i class="fa fa-clone mr-1"></i>{{ workflowStackedMaps ? 'Unstack Maps' : 'Stack Maps' }}
          </b-button>
        </div>

        <b-input-group size="sm" class="spawn-map-search">
          <b-input-group-prepend is-text class="bg-dark border-secondary">
            <i class="fa fa-search small"></i>
          </b-input-group-prepend>
          <input
            ref="mapSearchInput"
            type="text"
            :placeholder="mapSearchPlaceholder"
            class="form-control form-control-sm bg-dark text-white border-secondary"
            @input="handleMapSearchInput($event.target.value)"
          >
          <b-input-group-append>
            <b-form-select
              v-model="mapSearchMode"
              :options="mapSearchModeOptions"
              size="sm"
              class="spawn-map-search-mode bg-dark text-white border-secondary"
            ></b-form-select>
          </b-input-group-append>
        </b-input-group>

        <div v-if="selectedSearchEntity" class="spawn-search-context">
          <div class="text-muted small">{{ selectedSearchEntityLabel }}</div>
          <b-button size="sm" variant="outline-secondary" @click="clearSearchEntity">
            <i class="fa fa-times"></i>
          </b-button>
        </div>

        <div v-if="loading" class="spawn-sidebar-loading">
          <b-spinner small variant="info" label="Loading"></b-spinner>
          <span>{{ spawnsLoadingLabel }}</span>
        </div>

        <div
          ref="sidebarList"
          class="spawn-map-list"
          @scroll.passive="handleSidebarScroll"
        >
          <div
            v-if="sidebarItems.length"
            class="spawn-map-virtual-space"
            :style="{ height: virtualSidebarTotalHeight + 'px' }"
          >
            <div
              class="spawn-map-virtual-window"
              :style="{ transform: 'translateY(' + visibleSidebarTopOffset + 'px)' }"
            >
          <button
            v-for="row in visibleSidebarRows"
            :key="row.key"
            type="button"
            class="spawn-map-item"
            :class="{ active: row.item.map && row.item.map.name === activeMap, 'spawn-map-item-stacked': row.item.type === 'pokemonMap' || row.item.type === 'regionMap', 'spawn-map-item-unchanged': workflowView === 'wip' && row.item.map && row.item.map.hasChanges === false }"
            :style="{ height: row.height + 'px' }"
            :disabled="editMode"
            @click="selectSidebarItem(row.item)"
          >
            <template v-if="row.item.type === 'pokemonOption'">
              <span class="spawn-sidebar-entity">
                <span class="spawn-sidebar-sprite-wrap">
                  <img
                    v-if="!spriteErrors['search-' + row.item.dexID]"
                    class="spawn-sidebar-sprite"
                    :src="pokemonSpriteUrl(row.item.dexID)"
                    :alt="row.item.name"
                    loading="lazy"
                    decoding="async"
                    fetchpriority="low"
                    width="30"
                    height="30"
                    @error="handleSearchSpriteError(row.item)"
                  >
                  <i v-else class="fa fa-question"></i>
                </span>
                <span class="spawn-map-name" :class="{ 'spawn-membership-name': row.item.memberOnly }" v-html="highlightMapSearch(row.item.name)"></span>
                <span
                  v-if="showWarnings && row.item.repelInfo"
                  class="spawn-repel-inline-icon"
                  v-b-tooltip.html.hover
                  :title="repelInfoTooltip(row.item.repelInfo)"
                >
                  <img v-if="!iconErrors.repel" :src="repelIconUrl" alt="Repel" @error="markIconError('repel')">
                  <i v-else class="fa fa-shield"></i>
                </span>
              </span>
              <span class="badge badge-secondary">{{ row.item.mapCount }}</span>
            </template>

            <template v-else-if="row.item.type === 'regionOption'">
              <span class="spawn-map-name" v-html="highlightMapSearch(row.item.name)"></span>
              <span class="badge badge-secondary">{{ row.item.mapCount }}</span>
            </template>

            <template v-else-if="row.item.type === 'pokemonMap'">
              <span class="spawn-map-result">
                <span class="spawn-map-header-line">
                  <span class="spawn-map-name" v-html="highlightMapSearch(row.item.map.name)"></span>
                  <span class="spawn-map-counts">
                    <span class="badge badge-success">{{ row.item.map.land }}</span>
                    <span class="badge badge-info">{{ row.item.map.water }}</span>
                  </span>
                </span>
                <span class="spawn-pokemon-map-line">
                  <span class="spawn-sidebar-sprite-wrap">
                    <img
                      v-if="!spriteErrors['search-map-' + row.item.dexID + '-' + row.item.map.name]"
                      class="spawn-sidebar-sprite"
                      :src="pokemonSpriteUrl(row.item.dexID)"
                      :alt="row.item.pokemonName"
                      loading="lazy"
                      decoding="async"
                      fetchpriority="low"
                      width="30"
                      height="30"
                      @error="handleSearchMapSpriteError(row.item)"
                    >
                    <i v-else class="fa fa-question"></i>
                  </span>
                  <span :class="{ 'spawn-membership-name': row.item.memberOnly }">{{ row.item.pokemonName }}</span>
                  <span class="spawn-tier spawn-tier-compact" :class="['tier-group-' + row.item.tierGroup, { 'spawn-censored-value': censorRarityTiers }]">{{ row.item.tierLabel }}</span>
                  <span
                    v-if="showWarnings && row.item.repelInfo"
                    class="spawn-repel-inline-icon"
                    v-b-tooltip.html.hover
                    :title="repelInfoTooltip(row.item.repelInfo)"
                  >
                    <img v-if="!iconErrors.repel" :src="repelIconUrl" alt="Repel" @error="markIconError('repel')">
                    <i v-else class="fa fa-shield"></i>
                  </span>
                </span>
              </span>
            </template>

            <template v-else-if="row.item.type === 'regionMap'">
              <span class="spawn-map-result">
                <span class="spawn-map-header-line">
                  <span class="spawn-map-name" v-html="highlightMapSearch(row.item.map.name)"></span>
                  <span class="spawn-map-counts">
                    <span class="badge badge-success">{{ row.item.map.land }}</span>
                    <span class="badge badge-info">{{ row.item.map.water }}</span>
                  </span>
                </span>
                <span class="spawn-region-map-line" v-html="highlightMapSearch(row.item.region)"></span>
              </span>
            </template>

            <template v-else>
              <span class="spawn-map-name" v-html="highlightMapSearch(row.item.map.name)"></span>
              <span class="spawn-map-counts">
                <span class="badge badge-success">{{ row.item.map.land }}</span>
                <span class="badge badge-info">{{ row.item.map.water }}</span>
              </span>
            </template>
          </button>
            </div>
          </div>

          <div v-if="sidebarItems.length === 0" class="text-muted text-center py-4">
            No entries found.
          </div>
        </div>
      </aside>

      <main class="spawn-content" :class="{ 'spawn-content-editing': editMode, 'spawn-content-loading': mapSpawnsLoading }">
        <b-card no-body class="spawn-toolbar-card">
          <div class="spawn-toolbar">
            <div class="spawn-title-block">
              <div class="spawn-title-row">
                <i class="fa fa-map text-info"></i>
                <strong>Spawn Editor</strong>
              </div>
              <div class="spawn-map-title text-muted small">
                <span>{{ workflowStackedMaps ? stackedWorkflowTitle : (activeMap || 'No map selected') }}</span>
                <b-spinner
                  v-if="mapSpawnsLoading"
                  small
                  variant="info"
                  label="Loading spawns"
                  class="spawn-map-title-spinner"
                ></b-spinner>
                <span v-if="!workflowStackedMaps && selectedMapInfo"> - {{ selectedMapInfo.land }} land, {{ selectedMapInfo.water }} water</span>
              </div>
            </div>

            <div class="spawn-controls">
              <div class="spawn-control-group">
                <b-button-group size="sm">
                  <b-button :variant="displayMode === 'stacked' ? 'info' : 'outline-info'" @click="displayMode = 'stacked'">
                    <i class="fa fa-list mr-1"></i> Stacked
                  </b-button>
                  <b-button :variant="displayMode === 'land' ? 'success' : 'outline-success'" @click="displayMode = 'land'">
                    <i class="fa fa-tree mr-1"></i> Land
                  </b-button>
                  <b-button :variant="displayMode === 'water' ? 'primary' : 'outline-primary'" @click="displayMode = 'water'">
                    <i class="fa fa-tint mr-1"></i> Water
                  </b-button>
                </b-button-group>
              </div>

              <div class="spawn-control-group">
                <span class="spawn-control-label">Sort</span>
                <b-form-select
                  v-model="sortKey"
                  :options="sortOptions"
                  size="sm"
                  class="spawn-sort-select bg-dark text-white border-secondary"
                ></b-form-select>
                <b-button
                  size="sm"
                  variant="outline-info"
                  class="spawn-sort-direction"
                  @click="toggleSortDirection"
                >
                  <i class="fa mr-1" :class="sortDirection === 'asc' ? 'fa-sort-amount-asc' : 'fa-sort-amount-desc'"></i>
                  {{ sortDirection === 'asc' ? 'Asc' : 'Desc' }}
                </b-button>
              </div>

              <div class="spawn-control-group">
                <span class="spawn-control-label">Time</span>
                <b-button-group size="sm">
                  <b-button
                    v-for="filter in timeFilters"
                    :key="'time-filter-' + filter.key"
                    :variant="activeTimeFilter === filter.key ? 'info' : 'outline-info'"
                    @click="activeTimeFilter = filter.key"
                  >
                    {{ filter.label }}
                  </b-button>
                </b-button-group>
              </div>

              <b-button
                size="sm"
                class="spawn-membership-toggle"
                :variant="membershipActive ? 'info' : 'outline-secondary'"
                @click="membershipActive = !membershipActive"
              >
                <i class="fa mr-1" :class="membershipActive ? 'fa-toggle-on' : 'fa-toggle-off'"></i>
                Membership: {{ membershipActive ? 'Active' : 'Inactive' }}
              </b-button>

              <b-button
                size="sm"
                class="spawn-warning-toggle"
                :variant="showWarnings ? 'warning' : 'outline-secondary'"
                @click="showWarnings = !showWarnings"
              >
                <i class="fa mr-1" :class="showWarnings ? 'fa-eye' : 'fa-eye-slash'"></i>
                Warnings: {{ showWarnings ? 'On' : 'Off' }}
              </b-button>
            </div>
          </div>
        </b-card>

        <div v-if="isInitialSpawnsLoading" class="spawn-loading">
          <b-spinner variant="info" label="Loading"></b-spinner>
          <div class="text-muted mt-2">{{ spawnsLoadingLabel }}</div>
        </div>

        <div v-else-if="!activeMap" class="spawn-empty">
          <i class="fa fa-map-o"></i>
          <div>No spawn maps available.</div>
        </div>

        <template v-else>
          <div
            class="spawn-map-stage"
            :class="{ 'spawn-map-stage-with-background': mapImageReady, 'spawn-map-stage-loading': mapImageLoading }"
            :style="mapStageStyle"
          >
            <img
              v-if="activeMapImageUrl && !mapImageError"
              ref="mapStageImage"
              :key="activeMapImageKey"
              :src="activeMapImageUrl"
              :alt="activeMap + ' map background'"
              class="spawn-map-stage-image"
              loading="lazy"
              decoding="async"
              @load="handleMapImageLoad"
              @error="handleMapImageError"
            >
            <div v-if="mapImageLoading" class="spawn-map-loading" aria-label="Loading map background">
              <b-spinner small variant="info" label="Loading map background"></b-spinner>
            </div>
            <div v-if="workflowStackedMaps && workflowView !== 'live' && !editMode" class="spawn-workflow-stack">
            <div class="spawn-stack-toolbar">
              <div>
                <strong>{{ stackedWorkflowTitle }}</strong>
                <small class="text-muted d-block">{{ stackedWorkflowMapGroups.length }} maps shown together</small>
              </div>
              <div class="spawn-stack-actions">
                <b-button
                  v-if="workflowView === 'wip' && stackedOwnedWipBatchesWithChanges.length"
                  size="sm"
                  variant="info"
                  @click="moveStackedBatches('review')"
                >
                  Move all to Review
                </b-button>
                <b-button
                  v-if="(workflowView === 'wip' || workflowView === 'review') && stackedOwnedReadyBatchesWithChanges.length"
                  size="sm"
                  variant="success"
                  @click="moveStackedBatches('ready')"
                >
                  Move all to Ready for Upload
                </b-button>
                <b-button
                  v-if="(workflowView === 'review' || workflowView === 'ready') && stackedBatchesMovableBack.length"
                  size="sm"
                  variant="warning"
                  @click="moveStackedBatchesBackToWorkspace"
                >
                  Move all back to Workspace
                </b-button>
                <b-button
                  v-if="workflowView === 'review' && stackedReviewableBatches.length"
                  size="sm"
                  variant="outline-success"
                  @click="reviewStackedBatches('approved')"
                >
                  Approve all
                </b-button>
                <b-button
                  v-if="workflowView === 'review' && stackedReviewableBatches.length"
                  size="sm"
                  variant="outline-danger"
                  @click="reviewStackedBatches('rejected')"
                >
                  Reject all
                </b-button>
                <b-button
                  v-if="workflowView === 'ready' && workflowCanUploadReady && stackedReadyBatchesWithChanges.length"
                  size="sm"
                  variant="outline-success"
                  @click="uploadStackedReadyBatches"
                >
                  Upload all Spawns
                </b-button>
                <b-button
                  v-if="workflowView === 'ready' && workflowCanUploadReady && stackedReadyBatches.length"
                  size="sm"
                  variant="outline-danger"
                  @click="rejectReadyStackedBatches"
                >
                  Reject all Spawns
                </b-button>
                <b-button
                  v-if="workflowView === 'wip' && stackedOwnedWipBatches.length"
                  size="sm"
                  variant="danger"
                  @click="removeStackedWorkspaceBatches"
                >
                  Remove all from Workspace
                </b-button>
              </div>
            </div>

            <section
              v-for="group in stackedWorkflowMapGroups"
              :key="'stacked-map-' + group.batch.id"
              class="spawn-stack-map"
            >
              <div class="spawn-stack-map-header">
                <div>
                  <strong>{{ group.batch.mapName }}</strong>
                  <small class="text-muted d-block">Created by {{ group.batch.creatorName }} - {{ workflowStatusLabel(group.batch.status) }}</small>
                </div>
                <div class="spawn-stack-actions">
                  <b-button
                    v-if="batchCanMoveToStatus(group.batch, 'review')"
                    size="sm"
                    variant="info"
                    @click="moveBatch(group.batch, 'review')"
                  >
                    Move to Review
                  </b-button>
                  <b-button
                    v-if="batchCanMoveToStatus(group.batch, 'ready')"
                    size="sm"
                    variant="success"
                    @click="moveBatch(group.batch, 'ready')"
                  >
                    Move to Ready for Upload
                  </b-button>
                  <b-button
                    v-if="batchCanReview(group.batch)"
                    size="sm"
                    variant="outline-success"
                    @click="reviewBatch(group.batch, 'approved')"
                  >
                    Approve
                  </b-button>
                  <b-button
                    v-if="batchCanReview(group.batch)"
                    size="sm"
                    variant="outline-danger"
                    @click="reviewBatch(group.batch, 'rejected')"
                  >
                    Reject
                  </b-button>
                  <b-button
                    v-if="batchCanRemoveOrMoveBack(group.batch)"
                    size="sm"
                    :variant="group.batch.status === 'wip' ? 'danger' : 'warning'"
                    @click="removeBatch(group.batch)"
                  >
                    {{ group.batch.status === 'wip' ? 'Remove from Workspace' : 'Move back to Workspace' }}
                  </b-button>
                  <b-button
                    v-if="workflowCanUploadReady && group.batch.status === 'ready' && batchHasChanges(group.batch)"
                    size="sm"
                    variant="outline-success"
                    @click="uploadBatch(group.batch)"
                  >
                    Upload Spawns
                  </b-button>
                  <b-button
                    v-if="workflowCanUploadReady && group.batch.status === 'ready'"
                    size="sm"
                    variant="outline-danger"
                    @click="rejectReadyBatch(group.batch)"
                  >
                    Reject Spawns
                  </b-button>
                </div>
              </div>

              <div v-if="group.reviews.length" class="spawn-review-list">
                <span
                  v-for="review in group.reviews"
                  :key="'stack-review-' + review.id"
                  class="spawn-review-pill"
                  :class="'spawn-review-' + review.decision"
                >
                  {{ reviewDecisionLabel(review) }}
                  <button
                    v-if="canRemoveReview(review, group.batch)"
                    type="button"
                    class="spawn-review-remove"
                    @click.stop="removeOwnReview(group.batch)"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </span>
              </div>

              <div v-if="group.visibleSpawns.length === 0" class="spawn-empty-section">No spawns on this map.</div>
              <template v-else>
                <div v-if="group.land.length" class="spawn-stack-area-title">
                  <i class="fa fa-tree text-success"></i>Land Spawns <span class="badge badge-success">{{ group.land.length }}</span>
                </div>
                <div v-if="group.land.length" class="spawn-grid spawn-stack-card-grid">
                  <spawn-card
                    v-for="spawn in group.land"
                    :key="'stack-land-' + group.batch.id + '-' + spawnKey(spawn)"
                    :card-class="stackedSpawnCardClasses(spawn, 'spawn-card-land', group)"
                    :fighting-type-icon-url="fightingTypeIconUrl"
                    :get-repel-info="group.repelInfoGetter"
                    :get-repel-tooltip="group.repelTooltipGetter"
                    :helpers="group.cardHelpers"
                    :icon-errors="iconErrors"
                    :probability-label="group.probabilityLabel"
                    :repel-icon-url="repelIconUrl"
                    :show-warnings="showWarnings"
                    :spawn="spawn"
                    :sprite-error="!!spriteErrors['stack-' + group.batch.id + '-' + spawnKey(spawn)]"
                    :time-periods="timePeriods"
                    @sprite-error="handleStackSpriteError(group, spawn)"
                  ></spawn-card>
                </div>

                <div v-if="group.water.length" class="spawn-stack-area-title">
                  <i class="fa fa-tint text-info"></i>Water Spawns <span class="badge badge-info">{{ group.water.length }}</span>
                </div>
                <div v-if="group.water.length" class="spawn-grid spawn-stack-card-grid">
                  <spawn-card
                    v-for="spawn in group.water"
                    :key="'stack-water-' + group.batch.id + '-' + spawnKey(spawn)"
                    :card-class="stackedSpawnCardClasses(spawn, 'spawn-card-water', group)"
                    :fighting-type-icon-url="fightingTypeIconUrl"
                    :get-repel-info="group.repelInfoGetter"
                    :get-repel-tooltip="group.repelTooltipGetter"
                    :helpers="group.cardHelpers"
                    :icon-errors="iconErrors"
                    :probability-label="group.probabilityLabel"
                    :repel-icon-url="repelIconUrl"
                    :show-warnings="showWarnings"
                    :spawn="spawn"
                    :sprite-error="!!spriteErrors['stack-' + group.batch.id + '-' + spawnKey(spawn)]"
                    :time-periods="timePeriods"
                    water
                    @sprite-error="handleStackSpriteError(group, spawn)"
                  ></spawn-card>
                </div>
              </template>
            </section>
          </div>

          <div v-else-if="activeBatch && workflowView !== 'live' && !editMode" class="spawn-workflow-panel">
            <div class="spawn-workflow-meta">
              <div>
                <strong>{{ activeBatch.mapName }}</strong>
                <small class="text-muted d-block">Created by {{ activeBatch.creatorName }} - {{ workflowStatusLabel(activeBatch.status) }}</small>
              </div>
              <div class="spawn-workflow-actions">
                  <b-button
                    v-if="batchCanMoveToStatus(activeBatch, 'review')"
                    size="sm"
                    variant="info"
                    @click="moveActiveBatch('review')"
                  >
                    Move to Review
                </b-button>
                  <b-button
                    v-if="batchCanMoveToStatus(activeBatch, 'ready')"
                    size="sm"
                    variant="success"
                    @click="moveActiveBatch('ready')"
                  >
                    Move to Ready for Upload
                </b-button>
                <b-button
                  v-if="canReviewActiveBatch"
                  size="sm"
                  variant="outline-success"
                  @click="reviewActiveBatch('approved')"
                >
                  Approve
                </b-button>
                <b-button
                  v-if="canReviewActiveBatch"
                  size="sm"
                  variant="outline-danger"
                  @click="reviewActiveBatch('rejected')"
                >
                  Reject
                </b-button>
                <b-button
                  v-if="activeBatch.isOwner"
                  size="sm"
                  variant="warning"
                  @click="removeActiveBatch"
                >
                  {{ activeBatch.status === 'wip' ? 'Remove from Workspace' : 'Move back to Workspace' }}
                </b-button>
                <b-button
                  v-if="workflowCanUploadReady && activeBatch.status === 'ready' && activeBatchHasChanges"
                  size="sm"
                  variant="outline-success"
                  @click="uploadActiveBatch"
                >
                  Upload Spawns
                </b-button>
                <b-button
                  v-if="workflowCanUploadReady && activeBatch.status === 'ready'"
                  size="sm"
                  variant="outline-danger"
                  @click="rejectReadyActiveBatch"
                >
                  Reject Spawns
                </b-button>
              </div>
            </div>

            <div v-if="activeBatch.reviews && activeBatch.reviews.length" class="spawn-review-list">
              <span
                v-for="review in activeBatch.reviews"
                :key="'review-' + review.id"
                class="spawn-review-pill"
                :class="'spawn-review-' + review.decision"
              >
                {{ reviewDecisionLabel(review) }}
                <button
                  v-if="canRemoveReview(review)"
                  type="button"
                  class="spawn-review-remove"
                  @click.stop="removeOwnReview(activeBatch)"
                >
                  <i class="fa fa-times"></i>
                </button>
              </span>
            </div>

            <div v-if="activeBatchHasChanges" class="spawn-compare-toggle-row">
              <b-button
                size="sm"
                variant="secondary"
                @click="toggleComparisonExpanded"
              >
                <i class="fa mr-1" :class="comparisonExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>Comparison
              </b-button>
            </div>

            <div v-if="activeBatchHasChanges && comparisonExpanded" class="spawn-compare-table">
              <div class="spawn-compare-row spawn-compare-head">
                <span>Change</span>
                <span>Area</span>
                <span>Pokemon</span>
                <span>Live</span>
                <span>New</span>
              </div>
              <div
                v-for="row in activeComparisonRows"
                :key="row.key"
                class="spawn-compare-row"
              >
                <span>{{ row.type }}</span>
                <span>{{ row.area }}</span>
                <span>{{ row.pokemon }}</span>
                <span v-html="row.beforeHtml || escapeHtml(row.before)"></span>
                <span v-html="row.afterHtml || escapeHtml(row.after)"></span>
              </div>
            </div>
          </div>

          <b-alert v-if="!workflowStackedMaps && showWarnings && selfHarmingWarnings.length" show variant="warning" class="spawn-self-harm-warning">
            <div class="spawn-warning-title">
              <i class="fa fa-warning mr-1"></i>
              Self-harming move warning
            </div>
            <div v-for="warning in warningRows(selfHarmingWarnings, 'selfHarm', 6)" :key="warning.key" class="spawn-warning-line">
              <span v-html="warningHtml(warning)"></span>
            </div>
            <button
              v-if="hiddenWarningCount(selfHarmingWarnings, 'selfHarm', 6)"
              type="button"
              class="spawn-warning-more"
              @click="toggleWarningGroup('selfHarm')"
            >
              {{ warningGroupExpanded('selfHarm') ? 'Show fewer' : '+' + hiddenWarningCount(selfHarmingWarnings, 'selfHarm', 6) + ' more' }}
            </button>
          </b-alert>

          <b-alert v-if="!workflowStackedMaps && showWarnings && repelWarnings.length" show variant="warning" class="spawn-self-harm-warning">
            <div class="spawn-warning-title">
              <img v-if="!iconErrors.repel" class="spawn-warning-title-icon" :src="repelIconUrl" alt="Repel" @error="markIconError('repel')">
              <i v-else class="fa fa-shield mr-1"></i>
              Repel-trick warning
            </div>
            <div v-for="warning in warningRows(repelWarnings, 'repel', 8)" :key="warning.key" class="spawn-warning-line">
              <span v-html="warningHtml(warning)"></span>
            </div>
            <button
              v-if="hiddenWarningCount(repelWarnings, 'repel', 8)"
              type="button"
              class="spawn-warning-more"
              @click="toggleWarningGroup('repel')"
            >
              {{ warningGroupExpanded('repel') ? 'Show fewer' : '+' + hiddenWarningCount(repelWarnings, 'repel', 8) + ' more' }}
            </button>
          </b-alert>

          <section v-if="!workflowStackedMaps && showLand" class="spawn-section">
            <div class="spawn-section-header">
              <div class="spawn-section-title">
                <i class="fa fa-tree text-success"></i>
                <strong>Land Spawns</strong>
                <span class="badge badge-success">{{ landSpawns.length }}</span>
              </div>
              <div v-if="showSpawnSectionActions('land') || editMode" class="spawn-section-actions">
                <b-button
                  v-if="!editMode && canBeginEdit"
                  size="sm"
                  variant="warning"
                  @click="beginEdit"
                >
                  <i class="fa fa-edit mr-1"></i>Edit
                </b-button>
                <template v-if="editMode">
                  <b-button size="sm" variant="success" @click="openAddSpawnModal('land')">
                    <i class="fa fa-plus mr-1"></i>Add Spawns
                  </b-button>
                  <b-button v-if="editRows.length" size="sm" variant="danger" @click="deleteAllEditSpawns">
                    <i class="fa fa-trash mr-1"></i>Delete all Spawns
                  </b-button>
                </template>
              </div>
            </div>

            <b-alert v-if="showWarnings && landNatureConflictWarnings.length" show variant="warning" class="spawn-self-harm-warning">
              <div class="spawn-warning-title">
                <i class="fa fa-leaf mr-1"></i>
                Land shared nature warning
              </div>
              <div v-for="warning in warningRows(landNatureConflictWarnings, 'nature-land', 8)" :key="warning.key" class="spawn-warning-line">
                <span v-html="warningHtml(warning)"></span>
              </div>
              <button
                v-if="hiddenWarningCount(landNatureConflictWarnings, 'nature-land', 8)"
                type="button"
                class="spawn-warning-more"
                @click="toggleWarningGroup('nature-land')"
              >
                {{ warningGroupExpanded('nature-land') ? 'Show fewer' : '+' + hiddenWarningCount(landNatureConflictWarnings, 'nature-land', 8) + ' more' }}
              </button>
            </b-alert>

            <div v-if="landNatureStats.length" class="spawn-nature-summary">
              <button
                v-for="nature in landNatureStats"
                :key="'land-nature-stat-' + nature.name"
                type="button"
                class="spawn-nature-pill"
                :class="{ active: isNatureHighlightActive(nature.name, 'land') }"
                @click="toggleNatureHighlight(nature.name, 'land')"
              >
                <span>{{ nature.name }}</span>
                <strong>{{ formatNaturePercent(nature.percent) }}</strong>
                <small>{{ nature.count }}</small>
              </button>
            </div>

            <div v-if="landSpawns.length === 0" class="spawn-empty-section">No land spawns on this map.</div>
            <div v-else class="spawn-grid">
              <spawn-card
                v-for="spawn in renderedLandSpawns"
                :key="spawnKey(spawn)"
                :card-class="spawnCardClasses(spawn, 'spawn-card-land')"
                :edit-mode="editMode"
                :fighting-type-icon-url="fightingTypeIconUrl"
                :get-repel-info="repelInfoForSpawn"
                :get-repel-tooltip="repelTooltip"
                :helpers="spawnCardHelpers"
                :icon-errors="iconErrors"
                :item-lookup-options="itemLookupOptions"
                :probability-label="probabilityLabel"
                :repel-icon-url="repelIconUrl"
                :show-warnings="showWarnings"
                :spawn="spawn"
                :sprite-error="!!spriteErrors[spawnKey(spawn)]"
                :time-periods="timePeriods"
                @sprite-error="handleSpriteError(spawn)"
              ></spawn-card>
            </div>
          </section>

          <section v-if="!workflowStackedMaps && showWater" class="spawn-section">
            <div class="spawn-section-header">
              <div class="spawn-section-title">
                <i class="fa fa-tint text-info"></i>
                <strong>Water Spawns</strong>
                <span class="badge badge-info">{{ waterSpawns.length }}</span>
              </div>
              <div v-if="showSpawnSectionActions('water')" class="spawn-section-actions">
                <b-button
                  v-if="!editMode && canBeginEdit"
                  size="sm"
                  variant="warning"
                  @click="beginEdit"
                >
                  <i class="fa fa-edit mr-1"></i>Edit
                </b-button>
              </div>
            </div>

            <b-alert v-if="showWarnings && waterNatureConflictWarnings.length" show variant="warning" class="spawn-self-harm-warning">
              <div class="spawn-warning-title">
                <i class="fa fa-leaf mr-1"></i>
                Water shared nature warning
              </div>
              <div v-for="warning in warningRows(waterNatureConflictWarnings, 'nature-water', 8)" :key="warning.key" class="spawn-warning-line">
                <span v-html="warningHtml(warning)"></span>
              </div>
              <button
                v-if="hiddenWarningCount(waterNatureConflictWarnings, 'nature-water', 8)"
                type="button"
                class="spawn-warning-more"
                @click="toggleWarningGroup('nature-water')"
              >
                {{ warningGroupExpanded('nature-water') ? 'Show fewer' : '+' + hiddenWarningCount(waterNatureConflictWarnings, 'nature-water', 8) + ' more' }}
              </button>
            </b-alert>

            <div v-if="waterNatureStats.length" class="spawn-nature-summary">
              <button
                v-for="nature in waterNatureStats"
                :key="'water-nature-stat-' + nature.name"
                type="button"
                class="spawn-nature-pill"
                :class="{ active: isNatureHighlightActive(nature.name, 'water') }"
                @click="toggleNatureHighlight(nature.name, 'water')"
              >
                <span>{{ nature.name }}</span>
                <strong>{{ formatNaturePercent(nature.percent) }}</strong>
                <small>{{ nature.count }}</small>
              </button>
            </div>

            <div v-if="waterSpawns.length === 0" class="spawn-empty-section">No water spawns on this map.</div>
            <div v-else class="spawn-grid">
              <spawn-card
                v-for="spawn in renderedWaterSpawns"
                :key="spawnKey(spawn)"
                :card-class="spawnCardClasses(spawn, 'spawn-card-water')"
                :edit-mode="editMode"
                :fighting-type-icon-url="fightingTypeIconUrl"
                :get-repel-info="repelInfoForSpawn"
                :get-repel-tooltip="repelTooltip"
                :helpers="spawnCardHelpers"
                :icon-errors="iconErrors"
                :item-lookup-options="itemLookupOptions"
                :probability-label="probabilityLabel"
                :repel-icon-url="repelIconUrl"
                :show-warnings="showWarnings"
                :spawn="spawn"
                :sprite-error="!!spriteErrors[spawnKey(spawn)]"
                :time-periods="timePeriods"
                water
                @sprite-error="handleSpriteError(spawn)"
              ></spawn-card>
            </div>

          </section>
          </div>
        </template>

        <div v-if="editMode" class="spawn-edit-bottom-bar">
          <div>
            <strong>Edit mode active</strong>
            <small class="text-muted d-block">{{ editModeStatusText }}</small>
          </div>
          <div class="spawn-edit-bottom-actions">
            <b-button
              size="sm"
              variant="primary"
              :disabled="saveDraftDisabled"
              @click="saveDraft"
            >
              <i class="fa fa-save mr-1"></i>{{ saveDraftLabel }}
            </b-button>
            <b-button
              size="sm"
              variant="warning"
              :disabled="discardDraftDisabled"
              @click="discardDraftChanges"
            >
              <i class="fa fa-undo mr-1"></i>Discard
            </b-button>
            <b-button size="sm" variant="danger" @click="cancelEdit">
              <i class="fa fa-times mr-1"></i>Cancel
            </b-button>
          </div>
        </div>
      </main>
    </div>

    <b-modal
      v-model="newMapModal.visible"
      title="New Spawn Map"
      ok-title="Create"
      ok-variant="success"
      :ok-disabled="!canCreateNewMap"
      @ok.prevent="createNewMapDraft"
    >
      <b-form-group label="Map Name">
        <b-form-input v-model.trim="newMapModal.mapName" class="bg-dark text-white border-secondary"></b-form-input>
      </b-form-group>
    </b-modal>

    <b-modal
      v-model="addSpawnModal.visible"
      size="xl"
      modal-class="spawn-add-modal"
      title="Add Spawns"
      :ok-title="addSpawnOkTitle"
      ok-variant="success"
      :ok-disabled="addSpawnOkDisabled"
      @ok.prevent="addSelectedSpawns"
    >
      <template #modal-title>
        <div class="spawn-add-modal-title">
          <span>Add Spawns</span>
          <strong v-if="addSpawnModalMapName">{{ addSpawnModalMapName }}</strong>
        </div>
      </template>
      <div class="spawn-add-layout" :class="{ 'spawn-add-layout-with-existing': currentAddSpawnExistingRows.length }">
        <aside class="spawn-add-panel spawn-add-filter-panel">
          <div class="spawn-add-control-block">
            <div class="spawn-add-control-title">Area</div>
            <b-button-group size="sm" class="spawn-add-area-toggle">
              <b-button :variant="addSpawnModal.area === 'land' ? 'success' : 'outline-success'" @click="addSpawnModal.area = 'land'">
                <i class="fa fa-tree mr-1"></i>Land
              </b-button>
              <b-button :variant="addSpawnModal.area === 'surf' ? 'primary' : 'outline-primary'" @click="addSpawnModal.area = 'surf'">
                <i class="fa fa-tint mr-1"></i>Water
              </b-button>
            </b-button-group>
          </div>

          <div class="spawn-add-filter-actions">
            <b-button size="sm" variant="outline-secondary" @click="resetAddSpawnFilters">
              <i class="fa fa-times mr-1"></i>Clear Filters
            </b-button>
          </div>

          <section
            v-for="filter in addSpawnFilterGroups"
            :key="'add-filter-' + filter.key"
            class="spawn-add-filter-group"
            :class="{ disabled: !isAddSpawnFilterActive(filter.key) || isAddSpawnFilterDisabled(filter.key) }"
          >
            <div class="spawn-add-filter-heading">
              <button v-if="filter.options.length" type="button" class="spawn-add-collapse" :disabled="isAddSpawnFilterDisabled(filter.key)" @click="toggleAddSpawnFilterCollapsed(filter.key)">
                <i class="fa" :class="isAddSpawnFilterCollapsed(filter.key) ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
              </button>
              <span v-else class="spawn-add-collapse-spacer"></span>
              <label>
                <input
                  type="checkbox"
                  :disabled="isAddSpawnFilterDisabled(filter.key)"
                  :checked="isAddSpawnFilterActive(filter.key)"
                  @change="setAddSpawnFilterActive(filter.key, $event.target.checked)"
                >
                <span>{{ filter.label }}</span>
              </label>
              <small>{{ addSpawnFilterSelectedCount(filter.key) }}</small>
            </div>
            <div v-if="filter.options.length && !isAddSpawnFilterCollapsed(filter.key)" class="spawn-add-filter-options">
              <label
                v-for="option in filter.options"
                :key="'add-filter-' + filter.key + '-' + option.value"
                class="spawn-add-filter-option"
              >
                <input
                  type="checkbox"
                  :disabled="!isAddSpawnFilterActive(filter.key) || isAddSpawnFilterDisabled(filter.key)"
                  :checked="isAddSpawnFilterValueSelected(filter.key, option.value)"
                  @change="toggleAddSpawnFilterValue(filter.key, option.value, $event.target.checked)"
                >
                <span :class="{ 'spawn-censored-value': censorRarityTiers && filter.key === 'rarities' }">{{ addSpawnFilterOptionText(filter, option) }}</span>
              </label>
            </div>
          </section>
        </aside>

        <section class="spawn-add-panel spawn-add-available-panel">
          <div class="spawn-add-toolbar">
            <b-form-input
              v-model.trim="addSpawnModal.search"
              size="sm"
              placeholder="Search Pokemon..."
              class="bg-dark text-white border-secondary"
            ></b-form-input>
            <b-form-select
              v-model="addSpawnModal.sortKey"
              :options="addSpawnSortOptions"
              size="sm"
              class="bg-dark text-white border-secondary spawn-add-sort"
            ></b-form-select>
            <b-button size="sm" variant="outline-info" @click="addSpawnModal.sortDirection = addSpawnModal.sortDirection === 'asc' ? 'desc' : 'asc'">
              <i class="fa" :class="addSpawnModal.sortDirection === 'asc' ? 'fa-sort-amount-asc' : 'fa-sort-amount-desc'"></i>
            </b-button>
          </div>

          <div class="spawn-add-result-summary">
            {{ availableAddSpawnPokemon.length }} available - {{ selectedAddSpawnCount }} selected
          </div>

          <div class="spawn-add-grid">
            <button
              v-for="pokemon in availableAddSpawnPokemon"
              :key="'add-pokemon-' + pokemon.dexID"
              type="button"
              draggable="true"
              class="spawn-add-pokemon"
              @dblclick="addAddSpawnSelection(pokemon.dexID)"
              @dragstart="startAddSpawnDrag(pokemon, $event)"
              @dragend="endAddSpawnDrag"
            >
              <span class="spawn-add-sprite">
                <img
                  v-if="!spriteErrors['add-' + pokemon.dexID]"
                  :src="pokemonSpriteUrl(pokemon.dexID)"
                  :alt="pokemon.name"
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                  @error="handleAddSpawnSpriteError(pokemon)"
                >
                <i v-else class="fa fa-question"></i>
              </span>
              <span class="spawn-add-name">{{ pokemon.name }}</span>
              <small>#{{ pokemon.dexID }}</small>
            </button>
          </div>
        </section>

        <aside
          class="spawn-add-panel spawn-add-selected-panel"
          :class="{ dragging: addSpawnModal.draggingDexID }"
          @dragover.prevent
          @drop="dropAddSpawnPokemon"
        >
          <div class="spawn-add-selected-title">
            <span>Selected Pokemon</span>
            <strong>{{ selectedAddSpawnCount }}</strong>
          </div>
          <div v-if="selectedAddSpawnPokemon.length === 0" class="spawn-add-drop-empty">
            No Pokemon selected.
          </div>
          <div v-else class="spawn-add-selected-list">
            <div
              v-for="pokemon in selectedAddSpawnPokemon"
              :key="'selected-add-pokemon-' + pokemon.selectionKey"
              class="spawn-add-selected-row"
            >
              <span class="spawn-add-selected-sprite">
                <img
                  v-if="!spriteErrors['add-selected-' + pokemon.dexID]"
                  :src="pokemonSpriteUrl(pokemon.dexID)"
                  :alt="pokemon.name"
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                  @error="handleAddSpawnSelectedSpriteError(pokemon)"
                >
                <i v-else class="fa fa-question"></i>
              </span>
              <span class="spawn-add-selected-name">
                <strong>{{ pokemon.name }}</strong>
                <small>#{{ pokemon.dexID }} - <span :class="{ 'spawn-censored-value': censorRarityTiers }">{{ addSpawnPokemonTierDisplayLabel(pokemon.dexID) }}</span></small>
                <span class="spawn-add-probability-row">
                  <button
                    v-for="period in timePeriods"
                    :key="'selected-probability-' + pokemon.selectionKey + '-' + period.key"
                    type="button"
                    class="spawn-add-time-pill"
                    :class="{ active: isAddSpawnSelectedTimeActive(pokemon, period.key) }"
                    @click="toggleAddSpawnSelectedTime(pokemon.selectionKey, period.key)"
                  >
                    {{ period.label }} <span :class="{ 'spawn-censored-value': shouldCensorProbabilityLabel(addSpawnPreviewProbabilityLabel(pokemon, period.key)) }">{{ addSpawnPreviewProbabilityDisplayLabel(pokemon, period.key) }}</span>
                  </button>
                </span>
              </span>
              <button type="button" class="spawn-add-remove" @click="removeAddSpawnSelection(pokemon.selectionKey)">
                <i class="fa fa-times"></i>
              </button>
            </div>
          </div>
          <div class="spawn-add-control-block spawn-add-level-block">
            <div class="spawn-add-control-title">Level Range</div>
            <div class="spawn-add-level-edit">
              <b-form-input :value="draftLevelMinInputValue" type="text" inputmode="numeric" size="sm" class="bg-dark text-white border-secondary" :state="draftLevelMinInputState" @input.native="setDraftMinLevelFromEvent"></b-form-input>
              <span>-</span>
              <b-form-input :value="draftLevelMaxInputValue" type="text" inputmode="numeric" size="sm" class="bg-dark text-white border-secondary" :state="draftLevelMaxInputState" @input.native="setDraftMaxLevelFromEvent"></b-form-input>
            </div>
            <div v-if="selectedAddSpawnCount > 0 && !canUseAddSpawnLevelRange" class="spawn-add-level-warning">
              Enter a valid level range before adding spawns.
            </div>
          </div>
        </aside>

        <aside v-if="currentAddSpawnExistingRows.length" class="spawn-add-panel spawn-add-existing-panel">
          <div class="spawn-add-selected-title">
            <span>Existing Spawns</span>
            <strong>{{ activeAddSpawnExistingCount }}</strong>
          </div>
          <div class="spawn-add-selected-list">
            <div
              v-for="spawn in currentAddSpawnExistingRows"
              :key="'existing-add-spawn-' + spawnKey(spawn)"
              class="spawn-add-selected-row"
              :class="{ 'spawn-add-existing-removed': isAddSpawnExistingRemovalPending(spawn) }"
            >
              <span class="spawn-add-selected-sprite">
                <img
                  v-if="!spriteErrors['add-existing-' + spawnKey(spawn)]"
                  :src="pokemonSpriteUrl(spawn.dexID)"
                  :alt="pokemonName(spawn)"
                  loading="lazy"
                  decoding="async"
                  fetchpriority="low"
                  @error="handleAddSpawnExistingSpriteError(spawn)"
                >
                <i v-else class="fa fa-question"></i>
              </span>
              <span class="spawn-add-selected-name">
                <strong>{{ pokemonName(spawn) }}</strong>
                <small>
                  #{{ spawn.dexID }} - {{ areaLabel(spawn) }} -
                  <span :class="{ 'spawn-censored-value': censorRarityTiers }">{{ censorRarityTiers ? censoredTierLabel() : rarityGroupLabel(spawn) }}</span>
                </small>
                <span class="spawn-add-probability-row">
                  <button
                    v-for="period in timePeriods"
                    :key="'existing-probability-' + spawnKey(spawn) + '-' + period.key"
                    type="button"
                    class="spawn-add-time-pill"
                    :class="{ active: isAddSpawnExistingTimeActive(spawn, period.key) }"
                    :disabled="isAddSpawnExistingRemoved(spawn)"
                    @click="toggleAddSpawnExistingTime(spawn, period.key)"
                  >
                    {{ period.label }} <span :class="{ 'spawn-censored-value': shouldCensorProbabilityLabel(addSpawnExistingProbabilityLabel(spawn, period.key)) }">{{ addSpawnExistingProbabilityDisplayLabel(spawn, period.key) }}</span>
                  </button>
                </span>
                <em v-if="isAddSpawnExistingRemovalPending(spawn)">Will be removed on Add.</em>
              </span>
              <button
                type="button"
                class="spawn-add-remove"
                @click="toggleAddSpawnExistingRemoved(spawn)"
              >
                <i class="fa" :class="isAddSpawnExistingRemoved(spawn) ? 'fa-undo' : 'fa-times'"></i>
              </button>
            </div>
          </div>
        </aside>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import MovesIcon from '@/assets/Moves.png'
import Monsters from '@/views/pokemon/_monsters'
import SpawnCard from './SpawnCard.vue'
import SpawnEditorEditMixin from './SpawnEditorEditMixin'
import SpawnEditorWorkflowMixin from './SpawnEditorWorkflowMixin'

const TIME_PERIODS = [
  { key: 'morning', label: 'Morning', start: 4, end: 10 },
  { key: 'day', label: 'Day', start: 10, end: 20 },
  { key: 'night', label: 'Night', start: 20, end: 4 }
]

const TIME_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'morning', label: 'Morning' },
  { key: 'day', label: 'Day' },
  { key: 'night', label: 'Night' }
]

const SORT_OPTIONS = [
  { value: 'name', text: 'Name' },
  { value: 'dexID', text: 'DexID' },
  { value: 'rarity', text: 'Rarity' }
]

const ADD_SPAWN_SORT_OPTIONS = [
  { value: 'dexID', text: 'DexID' },
  { value: 'name', text: 'Name' },
  { value: 'rarity', text: 'Rarity' }
]

const ADD_SPAWN_FILTER_KEYS = ['types', 'rarities', 'habitats', 'evolutionStages', 'regions', 'events', 'natures', 'topUsage', 'hideAdded']

const MAP_SEARCH_MODE_OPTIONS = [
  { value: 'map', text: 'Map' },
  { value: 'pokemon', text: 'Pokemon' },
  { value: 'region', text: 'Region' }
]

const WORKFLOW_VIEW_OPTIONS = [
  { value: 'live', text: 'Live' },
  { value: 'wip', text: 'Workspace' },
  { value: 'review', text: 'Review' },
  { value: 'ready', text: 'Ready for Upload' }
]

const SIDEBAR_ROW_HEIGHT = 48
const SIDEBAR_STACKED_ROW_HEIGHT = 72
const SIDEBAR_OVERSCAN_PX = SIDEBAR_STACKED_ROW_HEIGHT * 6
const SIDEBAR_FALLBACK_VIEWPORT_HEIGHT = 480
const SPAWN_RENDER_INITIAL_COUNT = 48
const SPAWN_RENDER_CHUNK_SIZE = 160
const SPAWN_SEARCH_DEBOUNCE_MS = 35
const MAP_SWITCH_LOADING_MIN_MS = 0
const SPAWN_NAME_COLLATOR = new Intl.Collator(undefined, { numeric: true })
const searchText = value => String(value || '').toLowerCase()
const mapListKey = value => searchText(value).trim()

const EVOLUTION_STAGE_OPTIONS = [
  { value: 'baby', text: 'Baby Stage' },
  { value: 'middle', text: 'Middle Evolution' },
  { value: 'fully', text: 'Fully Evolved' }
]

const REGION_FILTERS = [
  { value: 'kanto', text: 'Kanto', ranges: [[1, 151]] },
  { value: 'johto', text: 'Johto', ranges: [[152, 251]] },
  { value: 'hoenn', text: 'Hoenn', ranges: [[252, 386]] },
  { value: 'sinnoh', text: 'Sinnoh', ranges: [[387, 493]] },
  { value: 'einall', text: 'Einall', ranges: [[494, 649]] },
  { value: 'kalos', text: 'Kalos', ranges: [[650, 721]] },
  { value: 'alola', text: 'Alola', ranges: [[722, 809], [3057, 3074]] },
  { value: 'galar', text: 'Galar', ranges: [[810, 905], [3112, 3122], [3128, 3130], [3243, 3248]] },
  { value: 'hisui', text: 'Hisui', ranges: [[3224, 3239]] },
  { value: 'paldea', text: 'Paldea', ranges: [[906, 1025], [3249, 3252]] }
]

const POKEAPI_ITEM_ICON_BASE = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/'
const POKEAPI_POKEMON_SPRITE_BASE = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/'
const REPEL_ICON_URL = POKEAPI_ITEM_ICON_BASE + 'repel.png'
const FIGHTING_TYPE_ICON_URL = MovesIcon

const FISHING_RODS = [
  { key: 'old', label: 'Old Rod', requiredRod: 1, icon: POKEAPI_ITEM_ICON_BASE + 'old-rod.png' },
  { key: 'good', label: 'Good Rod', requiredRod: 2, icon: POKEAPI_ITEM_ICON_BASE + 'good-rod.png' },
  { key: 'super', label: 'Super Rod', requiredRod: 3, icon: POKEAPI_ITEM_ICON_BASE + 'super-rod.png' }
]

const SPAWN_EDITOR_PREFS_KEY = 'spawn-editor-preferences'
const SANDSTORM_SAFE_TYPES = ['rock', 'steel', 'ground']

const LAND_RARITIES = [
  { tier: 'T1', value: 0.03 },
  { tier: 'T2', value: 0.01 },
  { tier: 'T3', value: 0.008 },
  { tier: 'T4', value: 0.006 },
  { tier: 'T5', value: 0.004 },
  { tier: 'T6', value: 0.002 },
  { tier: 'T7', value: 0.0009 },
  { tier: 'T8', value: 0.0007 },
  { tier: 'T9', value: 0.0005 },
  { tier: 'T9.5', value: 0.0003 },
  { tier: 'T10', value: 0 }
]

const WATER_RARITIES = [
  { tier: 'T1', value: 0.03 },
  { tier: 'T2', value: 0.01 },
  { tier: 'T3', value: 0.008 },
  { tier: 'T4', value: 0.006 },
  { tier: 'T5', value: 0.005 },
  { tier: 'T6', value: 0.003 },
  { tier: 'T7', value: 0.001 },
  { tier: 'T8', value: 0.0008 },
  { tier: 'T9', value: 0.0006 },
  { tier: 'T9.5', value: 0.0004 },
  { tier: 'T10', value: 0 }
]

const MONSTER_BY_ID = Object.create(null)
Monsters.forEach(monster => {
  MONSTER_BY_ID[Number(monster.ID)] = monster
})

export default {
  name: 'SpawnEditor',
  mixins: [
    SpawnEditorEditMixin,
    SpawnEditorWorkflowMixin
  ],
  components: {
    SpawnCard
  },
  data () {
    return {
      activeMap: '',
      activeBatchId: null,
      activeNatureHighlight: '',
      activeNatureHighlightArea: '',
      activeTimeFilter: 'all',
      activeTimeKeyCache: Object.create(null),
      addSpawnModal: {
        visible: false,
        area: 'land',
        search: '',
        sortKey: 'dexID',
        sortDirection: 'asc',
        selected: {},
        selectedOrder: [],
        draggingDexID: null,
        removedExisting: {},
        collapsedFilters: ADD_SPAWN_FILTER_KEYS.reduce((result, key) => {
          result[key] = true
          return result
        }, {}),
        activeFilters: {
          types: true,
          rarities: true,
          habitats: true,
          evolutionStages: true,
          regions: true,
          events: true,
          natures: true,
          topUsage: false,
          hideAdded: true
        },
        filters: {
          types: [],
          rarities: [],
          habitats: [],
          evolutionStages: [],
          regions: [],
          events: [],
          natures: []
        },
        existingTimeOverrides: {},
        nextSelectionId: 1
      },
      comparisonExpanded: false,
      displayMode: 'stacked',
      draftLevelMax: null,
      draftLevelMaxInput: '',
      draftLevelMin: null,
      draftLevelMinInput: '',
      editMode: false,
      editRows: [],
      expandedWarningGroups: {
        'nature-land': false,
        'nature-water': false,
        repel: false,
        selfHarm: false
      },
      fightingTypeIconUrl: FIGHTING_TYPE_ICON_URL,
      iconErrors: {},
      loading: false,
      loadingTimer: null,
      mainRenderFrame: null,
      mainRenderCounts: {
        land: SPAWN_RENDER_INITIAL_COUNT,
        water: SPAWN_RENDER_INITIAL_COUNT
      },
      mapImageError: false,
      mapImageLoadedUrl: '',
      mapSwitchFrame: null,
      mapSwitchLoading: false,
      mapSwitchStartedAt: 0,
      mapSwitchTimer: null,
      mapAnalysisFrame: null,
      mapAnalysisReady: true,
      mapSearchMode: 'map',
      mapSearchModeOptions: MAP_SEARCH_MODE_OPTIONS,
      mapSearch: '',
      mapSearchInput: '',
      mapSearchTimer: null,
      pendingMapSearchInput: '',
      membershipActive: false,
      moveCache: Object.create(null),
      newMapModal: {
        visible: false,
        mapName: ''
      },
      pendingDraftSave: null,
      pendingDraftSaveAck: null,
      pendingNewMapName: '',
      pendingWorkflowSelection: null,
      pokemonMapItemsCache: Object.create(null),
      repelIconUrl: REPEL_ICON_URL,
      repelMapCache: Object.create(null),
      referenceAnalysisFrame: null,
      referenceAnalysisRequestPending: false,
      safeLevelRangeCache: Object.create(null),
      selectedSearchEntity: null,
      selfHarmingUnsafeLevelCache: Object.create(null),
      showWarnings: false,
      sidebarResizeObserver: null,
      sidebarScrollTop: 0,
      sidebarViewportHeight: 0,
      sortDirection: 'asc',
      sortKey: 'name',
      sortOptions: SORT_OPTIONS,
      spriteErrors: {},
      timeFilters: TIME_FILTERS,
      timePeriods: TIME_PERIODS,
      workflowStackedMaps: false,
      workflowView: 'live'
    }
  },
  computed: {
    ...mapGetters({
      authUser: 'authUser',
      spawns: 'spawns',
      spawnsUpdatedAt: 'spawnsUpdatedAt',
      liveSpawnMaps: 'spawnLiveMaps',
      liveRowsByDexID: 'spawnLiveRowsByDexID',
      liveRowsByMap: 'spawnLiveRowsByMap',
      liveRowsByMapListKey: 'spawnLiveRowsByMapListKey',
      formMapping: 'formMapping',
      referenceData: 'spawnReferenceData',
      referenceLookups: 'spawnReferenceLookups',
      referenceDataUpdatedAt: 'spawnReferenceDataUpdatedAt',
      spawnReferenceAnalysisLoaded: 'spawnReferenceAnalysisLoaded',
      lastDraftSave: 'spawnLastDraftSave',
      spawnMoves: 'spawnMoves',
      spawnPokemonCatalog: 'spawnPokemonCatalog',
      spawnUsageRanks: 'spawnUsageRanks',
      workflowBatches: 'spawnWorkflowBatches',
      workflowCanUploadReady: 'spawnWorkflowCanUploadReady',
      spawnCanAddLegendary: 'spawnCanAddLegendary',
      workflowUpdatedAt: 'spawnWorkflowUpdatedAt'
    }),
    workflowBatchCounts () {
      return (this.workflowBatches || []).reduce((counts, batch) => {
        const status = String(batch.status || '')
        counts[status] = (counts[status] || 0) + 1
        return counts
      }, Object.create(null))
    },
    visibleWorkflowViewOptions () {
      return WORKFLOW_VIEW_OPTIONS.filter(option => {
        if (option.value === 'live' || option.value === 'wip') return true
        return Number(this.workflowBatchCounts[option.value] || 0) > 0
      })
    },
    workflowStackAvailable () {
      return this.workflowView !== 'live' && this.currentWorkflowBatches.filter(batch => this.batchHasChanges(batch)).length > 1
    },
    stackedWorkflowTitle () {
      return this.workflowStatusLabel(this.workflowView) + ' stacked'
    },
    stackedOwnedBatches () {
      return this.currentWorkflowBatches.filter(batch => batch.isOwner)
    },
    stackedOwnedWipBatches () {
      return this.stackedOwnedBatches.filter(batch => batch.status === 'wip')
    },
    stackedOwnedWipBatchesWithChanges () {
      return this.stackedOwnedBatches.filter(batch => batch.status === 'wip' && this.batchHasChanges(batch))
    },
    stackedOwnedReadyBatchesWithChanges () {
      return this.stackedOwnedBatches.filter(batch => (batch.status === 'wip' || batch.status === 'review') && this.batchHasChanges(batch))
    },
    stackedBatchesMovableBack () {
      return this.currentWorkflowBatches.filter(batch => this.batchCanMoveBackToWorkspace(batch))
    },
    stackedReviewableBatches () {
      return this.currentWorkflowBatches.filter(batch => this.batchCanReview(batch))
    },
    stackedReadyBatches () {
      return this.currentWorkflowBatches.filter(batch => batch.status === 'ready')
    },
    stackedReadyBatchesWithChanges () {
      return this.currentWorkflowBatches.filter(batch => batch.status === 'ready' && this.batchHasChanges(batch))
    },
    stackedWorkflowMapGroups () {
      return this.currentWorkflowBatches.map(batch => {
        const spawns = (batch.spawns || []).slice().sort(this.compareSpawns)
        const visibleSpawns = spawns.filter(spawn => this.passesActiveFilters(spawn))
        const group = {
          batch,
          reviews: batch.reviews || [],
          spawns,
          visibleSpawns,
          land: visibleSpawns.filter(spawn => !this.isWaterSpawn(spawn)),
          water: visibleSpawns.filter(spawn => this.isWaterSpawn(spawn)),
          probabilityTotals: this.buildSpawnProbabilityTotalsByAreaPeriod(spawns),
          fishingTotals: this.buildFishingProbabilityTotalsByRodPeriod(spawns),
          repelInfoByKey: this.showWarnings && this.mapAnalysisReady ? this.buildRepelInfoBySpawnKey(spawns) : Object.create(null)
        }
        group.cardHelpers = Object.assign({}, this.spawnCardHelpers, {
          fishingChanceRows: spawn => this.fishingChanceRowsWithTotals(spawn, group.fishingTotals)
        })
        group.probabilityLabel = (spawn, key) => this.probabilityLabelWithTotals(spawn, key, group.probabilityTotals)
        group.repelInfoGetter = spawn => this.stackedRepelInfoForSpawn(spawn, group)
        group.repelTooltipGetter = spawn => this.stackedRepelTooltip(spawn, group)
        return group
      }).filter(group => group.visibleSpawns.length > 0)
    },
    canBeginEdit () {
      if (this.editMode || !this.activeMap) return false
      return this.workflowView === 'live' || (this.workflowView === 'wip' && this.activeBatch && this.activeBatch.isOwner)
    },
    censorRarityTiers () {
      return !this.hasPermission('SpawnsRarityTierView')
    },
    liveSpawnsByMap () {
      return this.liveRowsByMap || Object.create(null)
    },
    liveSpawnsByMapListKey () {
      return this.liveRowsByMapListKey || Object.create(null)
    },
    spawnsByMap () {
      if (this.workflowView === 'live') return this.liveSpawnsByMap
      const grouped = Object.create(null)
      ;(this.displayRows || []).forEach(spawn => {
        const name = spawn.map || 'Unknown'
        if (!grouped[name]) grouped[name] = []
        grouped[name].push(spawn)
      })
      return grouped
    },
    spawnsByMapListKey () {
      if (this.workflowView === 'live') return this.liveSpawnsByMapListKey
      const grouped = Object.create(null)
      Object.keys(this.spawnsByMap).forEach(name => {
        const key = mapListKey(name)
        if (!key) return
        if (!grouped[key]) grouped[key] = []
        grouped[key] = grouped[key].concat(this.spawnsByMap[name])
      })
      return grouped
    },
    spawnsByDexID () {
      if (this.workflowView === 'live') return this.liveRowsByDexID || Object.create(null)
      const grouped = Object.create(null)
      ;(this.sidebarSearchRows || []).forEach(spawn => {
        const dexID = Number(spawn.dexID || 0)
        if (!dexID) return
        if (!grouped[dexID]) grouped[dexID] = []
        grouped[dexID].push(spawn)
      })
      return grouped
    },
    sidebarSearchRows () {
      if (this.workflowView === 'live') return this.spawns || []
      return (this.currentWorkflowBatches || []).reduce((rows, batch) => rows.concat(batch.spawns || []), [])
    },
    displayRows () {
      if (this.workflowView === 'live') return this.spawns || []
      if (this.activeBatch) return this.activeBatch.spawns || []
      return []
    },
    maps () {
      if (this.workflowView !== 'live') {
        return this.currentWorkflowBatches.map(batch => {
          const rows = batch.spawns || []
          const map = {
            name: batch.mapName || 'Unknown',
            batchID: batch.id,
            creatorName: batch.creatorName,
            hasChanges: this.batchHasChanges(batch),
            status: batch.status,
            land: 0,
            searchName: searchText(batch.mapName || 'Unknown'),
            water: 0,
            total: rows.length
          }
          rows.forEach(spawn => {
            if (this.isWaterSpawn(spawn)) {
              map.water++
            } else {
              map.land++
            }
          })
          return map
        })
      }
      const seenNames = Object.create(null)
      const maps = []
      ;(this.liveSpawnMaps || []).forEach(map => {
        const key = mapListKey(map.name)
        if (!key || seenNames[key]) return
        seenNames[key] = true
        maps.push(map)
      })
      this.referenceMapNames.forEach(name => {
        const key = mapListKey(name)
        if (!key || seenNames[key]) return
        seenNames[key] = true
        maps.push({ name: name, searchName: searchText(name), land: 0, water: 0, total: 0 })
      })
      return maps.sort((a, b) => SPAWN_NAME_COLLATOR.compare(a.name, b.name))
    },
    referenceMapNames () {
      const regions = this.referenceData && this.referenceData.mapRegions ? this.referenceData.mapRegions : {}
      const seen = Object.create(null)
      Object.keys(regions).forEach(region => {
        ;(regions[region] || []).forEach(mapName => {
          const name = String(mapName || '').trim()
          if (name) seen[name] = true
        })
      })
      return Object.keys(seen).sort((a, b) => SPAWN_NAME_COLLATOR.compare(a, b))
    },
    mapByName () {
      return this.maps.reduce((result, map) => {
        result[map.name] = map
        return result
      }, Object.create(null))
    },
    mapByLowerName () {
      return this.maps.reduce((result, map) => {
        const key = mapListKey(map.name)
        if (!result[key] || Number(map.total || 0) > Number(result[key].total || 0)) {
          result[key] = map
        }
        return result
      }, Object.create(null))
    },
    pokemonOptionsByName () {
      return this.pokemonOptions.reduce((result, option) => {
        result[option.searchName] = option
        return result
      }, Object.create(null))
    },
    regionOptionsByName () {
      return this.regionOptions.reduce((result, option) => {
        result[option.searchName] = option
        return result
      }, Object.create(null))
    },
    filteredMaps () {
      const search = this.mapSearchText
      if (!search) return this.maps
      return this.maps.filter(map => map.searchName.indexOf(search) !== -1)
    },
    mapSearchText () {
      return searchText(this.mapSearch).trim()
    },
    sidebarSummary () {
      if (this.workflowView !== 'live') {
        return this.currentWorkflowBatches.length + ' batches'
      }
      const items = this.sidebarItems
      if (this.mapSearchMode === 'pokemon') {
        if (this.selectedSearchEntity || this.exactPokemonSearchMatch()) return items.length + ' maps'
        return this.pokemonOptions.length + ' Pokemon'
      }
      if (this.mapSearchMode === 'region') {
        if (this.selectedSearchEntity || this.exactRegionSearchMatch()) return items.length + ' maps'
        if (this.mapSearchText) {
          const mapCount = items.reduce((count, item) => count + (item.type === 'regionMap' ? 1 : 0), 0)
          return mapCount ? mapCount + ' maps' : items.length + ' regions'
        }
        return this.regionOptions.length + ' regions'
      }
      return this.maps.length + ' with spawns'
    },
    isInitialSpawnsLoading () {
      return this.loading && !this.spawnsUpdatedAt
    },
    spawnsLoadingLabel () {
      return this.spawnsUpdatedAt ? 'Refreshing spawns...' : 'Loading spawns...'
    },
    canCreateNewMap () {
      return String(this.newMapModal.mapName || '').trim().length > 0
    },
    addSpawnModalMapName () {
      return this.activeMap || (this.activeBatch && this.activeBatch.mapName) || ''
    },
    activeMapImageUrl () {
      if (!this.activeMap || this.workflowStackedMaps) return ''
      return '/maps/' + encodeURIComponent(this.activeMap)
    },
    activeMapImageKey () {
      return [this.workflowView, this.activeBatchId || 'live', this.activeMapImageUrl].join('|')
    },
    mapImageReady () {
      return !!(this.activeMapImageUrl && !this.mapImageError && this.mapImageLoadedUrl === this.activeMapImageUrl)
    },
    mapImageLoading () {
      return !!(this.activeMapImageUrl && !this.mapImageError && this.mapImageLoadedUrl !== this.activeMapImageUrl)
    },
    mapStageStyle () {
      if (!this.mapImageReady) return null
      return {
        '--spawn-map-image': 'url("' + this.activeMapImageUrl + '")'
      }
    },
    mapSearchPlaceholder () {
      if (this.mapSearchMode === 'pokemon') return this.selectedSearchEntity ? 'Filter maps...' : 'Search Pokemon...'
      if (this.mapSearchMode === 'region') return this.selectedSearchEntity ? 'Filter maps...' : 'Search region...'
      return 'Search map...'
    },
    selectedSearchEntityLabel () {
      if (!this.selectedSearchEntity) return ''
      if (this.selectedSearchEntity.type === 'pokemon') return 'Pokemon: ' + this.selectedSearchEntity.name
      if (this.selectedSearchEntity.type === 'region') return 'Region: ' + this.selectedSearchEntity.name
      return ''
    },
    sidebarItems () {
      if (this.mapSearchMode === 'pokemon') {
        if (this.selectedSearchEntity && this.selectedSearchEntity.type === 'pokemon') {
          return this.pokemonMapItems(this.selectedSearchEntity)
        }
        const exactPokemon = this.exactPokemonSearchMatch()
        if (exactPokemon) {
          return this.pokemonMapItems({ type: 'pokemon', dexID: exactPokemon.dexID, name: exactPokemon.name })
        }
        return this.filteredPokemonOptions
      }
      if (this.mapSearchMode === 'region') {
        if (this.selectedSearchEntity && this.selectedSearchEntity.type === 'region') {
          return this.regionMapItems(this.selectedSearchEntity)
        }
        const exactRegion = this.exactRegionSearchMatch()
        if (exactRegion) {
          return this.regionMapItems({ type: 'region', name: exactRegion.name })
        }
        return this.regionSidebarItems
      }
      return this.filteredMaps.map(map => ({ type: 'map', map }))
    },
    sidebarVirtualRows () {
      let top = 0
      return this.sidebarItems.map(item => {
        const height = this.sidebarItemHeight(item)
        const row = {
          item,
          key: this.sidebarItemKey(item),
          top,
          height
        }
        top += height
        return row
      })
    },
    virtualSidebarTotalHeight () {
      const rows = this.sidebarVirtualRows
      if (!rows.length) return 0
      const last = rows[rows.length - 1]
      return last.top + last.height
    },
    visibleSidebarRows () {
      const rows = this.sidebarVirtualRows
      if (!rows.length) return []

      const viewportHeight = this.sidebarViewportHeight || SIDEBAR_FALLBACK_VIEWPORT_HEIGHT
      const minTop = Math.max(0, this.sidebarScrollTop - SIDEBAR_OVERSCAN_PX)
      const maxBottom = this.sidebarScrollTop + viewportHeight + SIDEBAR_OVERSCAN_PX

      let low = 0
      let high = rows.length
      while (low < high) {
        const middle = Math.floor((low + high) / 2)
        if (rows[middle].top + rows[middle].height < minTop) {
          low = middle + 1
        } else {
          high = middle
        }
      }

      let end = low
      while (end < rows.length && rows[end].top < maxBottom) {
        end++
      }

      return rows.slice(low, end)
    },
    visibleSidebarTopOffset () {
      return this.visibleSidebarRows.length ? this.visibleSidebarRows[0].top : 0
    },
    pokemonOptions () {
      return Object.keys(this.spawnsByDexID)
        .map(dexID => {
          const rows = this.spawnsByDexID[dexID] || []
          const maps = Object.create(null)
          const name = rows.length ? this.pokemonName(rows[0]) : 'Unknown Pokemon'
          rows.forEach(spawn => {
            maps[mapListKey(spawn.map || 'Unknown')] = true
          })
          return {
            type: 'pokemonOption',
            dexID: Number(dexID),
            name,
            searchName: searchText(name),
            searchText: searchText(name) + ' #' + dexID,
            mapCount: Object.keys(maps).length,
            memberOnly: rows.length > 0 && rows.every(spawn => spawn.memberOnly),
            repelInfo: null
          }
        })
        .sort((a, b) => SPAWN_NAME_COLLATOR.compare(a.name, b.name))
    },
    filteredPokemonOptions () {
      const search = this.mapSearchText
      if (!search) return this.pokemonOptions
      return this.pokemonOptions.filter(option => option.searchText.indexOf(search) !== -1)
    },
    regionOptions () {
      const regions = this.referenceData && this.referenceData.mapRegions ? this.referenceData.mapRegions : {}
      return Object.keys(regions)
        .sort((a, b) => SPAWN_NAME_COLLATOR.compare(a, b))
        .map(region => {
          const regionMaps = regions[region] || []
          const mapItems = regionMaps
            .map(mapName => this.mapByLowerName[mapListKey(mapName)])
            .filter(map => map && Number(map.total || 0) > 0)
          return {
            type: 'regionOption',
            name: region,
            searchName: searchText(region),
            mapItems,
            mapCount: mapItems.length
          }
        })
        .filter(region => region.mapCount > 0)
    },
    filteredRegionOptions () {
      const search = this.mapSearchText
      if (!search) return this.regionOptions
      return this.regionOptions.filter(option => option.searchName.indexOf(search) !== -1)
    },
    regionSidebarItems () {
      const search = this.mapSearchText
      if (!search) return this.regionOptions
      const items = []
      this.regionOptions.forEach(region => {
        const regionMatches = region.searchName.indexOf(search) !== -1
        const matchingMaps = region.mapItems.filter(map => map.searchName.indexOf(search) !== -1)
        if (!regionMatches && !matchingMaps.length) return
        items.push(Object.assign({}, region, {
          mapCount: regionMatches ? region.mapCount : matchingMaps.length
        }))
        matchingMaps.forEach(map => {
          items.push({
            type: 'regionMap',
            region: region.name,
            map
          })
        })
      })
      return items
    },
    selectedMapInfo () {
      return this.mapByName[this.activeMap] || null
    },
    currentWorkflowBatches () {
      return (this.workflowBatches || [])
        .filter(batch => batch.status === this.workflowView)
        .slice()
        .sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
    },
    activeBatch () {
      if (this.workflowView === 'live') return null
      const byID = (this.currentWorkflowBatches || []).find(batch => Number(batch.id) === Number(this.activeBatchId))
      if (byID) return byID
      return (this.currentWorkflowBatches || [])[0] || null
    },
    canReviewActiveBatch () {
      return !!(this.activeBatch && this.activeBatch.canReview)
    },
    selectedSpawns () {
      if (this.editMode) return this.editRows
      if (this.workflowView !== 'live') return this.activeBatch ? (this.activeBatch.spawns || []) : []
      return this.spawnsForMapListName(this.activeMap)
    },
    selectedSpawnBuckets () {
      const buckets = {
        visible: [],
        analysis: [],
        land: [],
        water: [],
        landNature: [],
        waterNature: [],
        landNatureAnalysis: [],
        waterNatureAnalysis: []
      }
      this.selectedSpawns.forEach(spawn => {
        const water = this.isWaterSpawn(spawn)
        const fishingOnly = water && this.isFishingOnly(spawn)
        const membershipVisible = this.membershipActive || !spawn.memberOnly
        if (membershipVisible) {
          buckets.analysis.push(spawn)
          if (water) {
            if (!fishingOnly) buckets.waterNatureAnalysis.push(spawn)
          } else {
            buckets.landNatureAnalysis.push(spawn)
          }
        }
        if (!this.passesActiveFilters(spawn)) return
        buckets.visible.push(spawn)
        if (water) {
          buckets.water.push(spawn)
          if (!fishingOnly) buckets.waterNature.push(spawn)
        } else {
          buckets.land.push(spawn)
          buckets.landNature.push(spawn)
        }
      })
      buckets.land.sort(this.compareSpawns)
      buckets.water.sort(this.compareSpawns)
      return buckets
    },
    visibleSelectedSpawns () {
      return this.selectedSpawnBuckets.visible
    },
    analysisSpawns () {
      return this.selectedSpawnBuckets.analysis
    },
    landNatureSpawns () {
      return this.selectedSpawnBuckets.landNature
    },
    waterNatureSpawns () {
      return this.selectedSpawnBuckets.waterNature
    },
    landNatureAnalysisSpawns () {
      return this.selectedSpawnBuckets.landNatureAnalysis
    },
    waterNatureAnalysisSpawns () {
      return this.selectedSpawnBuckets.waterNatureAnalysis
    },
    landSpawns () {
      return this.selectedSpawnBuckets.land
    },
    waterSpawns () {
      return this.selectedSpawnBuckets.water
    },
    renderedLandSpawns () {
      if (this.renderAllSpawnCards) return this.landSpawns
      return this.landSpawns.slice(0, this.mainRenderCounts.land)
    },
    renderedWaterSpawns () {
      if (this.renderAllSpawnCards) return this.waterSpawns
      return this.waterSpawns.slice(0, this.mainRenderCounts.water)
    },
    renderAllSpawnCards () {
      return this.workflowStackedMaps
    },
    mainRenderComplete () {
      if (this.renderAllSpawnCards) return true
      const landDone = !this.showLand || this.mainRenderCounts.land >= this.landSpawns.length
      const waterDone = !this.showWater || this.mainRenderCounts.water >= this.waterSpawns.length
      return landDone && waterDone
    },
    mainRenderPrimed () {
      if (this.renderAllSpawnCards) return true
      const landReady = !this.showLand || this.landSpawns.length === 0 || this.mainRenderCounts.land > 0
      const waterReady = !this.showWater || this.waterSpawns.length === 0 || this.mainRenderCounts.water > 0
      return landReady && waterReady
    },
    mapSpawnsLoading () {
      return !!(this.activeMap && !this.workflowStackedMaps && (this.mapSwitchLoading || !this.mainRenderPrimed))
    },
    showLand () {
      return this.displayMode === 'stacked' || this.displayMode === 'land'
    },
    showWater () {
      return this.displayMode === 'stacked' || this.displayMode === 'water'
    },
    spawnCardHelpers () {
      return {
        applySafeLevelRange: this.applySafeLevelRange,
        deleteEditSpawn: this.deleteEditSpawn,
        fishingChanceRows: this.fishingChanceRows,
        formLabels: this.formLabels,
        hasItem: this.hasItem,
        isFishable: this.isFishable,
        isFishingOnly: this.isFishingOnly,
        isTimeActive: this.isTimeActive,
        itemDropLabel: this.itemDropLabel,
        itemInputState: this.itemInputState,
        itemName: this.itemName,
        itemNameInputValue: this.itemNameInputValue,
        markIconError: this.markIconError,
        moveTooltip: this.moveTooltip,
        pokemonName: this.pokemonName,
        pokemonSpriteUrl: this.pokemonSpriteUrl,
        censorRarityTiers: () => this.censorRarityTiers,
        censoredProbabilityLabel: this.censoredProbabilityLabel,
        censoredRarityLabel: this.censoredRarityLabel,
        censoredTierLabel: this.censoredTierLabel,
        rarityGroupKey: this.rarityGroupKey,
        rarityGroupLabel: this.rarityGroupLabel,
        rarityTier: this.rarityTier,
        rarityTierSelectOptions: this.rarityTierSelectOptions,
        rawRarity: this.rawRarity,
        rodIconForSpawn: this.rodIconForSpawn,
        rodLabel: this.rodLabel,
        safeLevelRangeOptions: this.safeLevelRangeOptions,
        selectSpawnItemOption: this.selectSpawnItemOption,
        selfHarmingTooltip: this.selfHarmingTooltip,
        shouldCensorProbabilityLabel: this.shouldCensorProbabilityLabel,
        setSpawnFishOnly: this.setSpawnFishOnly,
        setSpawnItemDropChanceFromEvent: this.setSpawnItemDropChanceFromEvent,
        setSpawnItemName: this.setSpawnItemName,
        setSpawnMaxLevelFromEvent: this.setSpawnMaxLevelFromEvent,
        setSpawnMinLevelFromEvent: this.setSpawnMinLevelFromEvent,
        setSpawnTimeActive: this.setSpawnTimeActive,
        spawnHasSelfHarmingWarning: this.spawnHasSelfHarmingWarning,
        spawnItemDropChanceInputValue: this.spawnItemDropChanceInputValue,
        spawnKey: this.spawnKey,
        spawnMaxLevelInputValue: this.spawnMaxLevelInputValue,
        spawnMaxLevelInputState: this.spawnMaxLevelInputState,
        spawnMinLevelInputValue: this.spawnMinLevelInputValue,
        spawnMinLevelInputState: this.spawnMinLevelInputState,
        spawnRarityInputValue: this.spawnRarityInputValue,
        preventEditInputWheel: this.preventEditInputWheel,
        updateSpawnRarityFromTier: this.updateSpawnRarityFromTier
      }
    },
    spawnProbabilityTotalsByAreaPeriod () {
      return this.buildSpawnProbabilityTotalsByAreaPeriod(this.selectedSpawns)
    },
    fishingProbabilityTotalsByRodPeriod () {
      return this.buildFishingProbabilityTotalsByRodPeriod(this.selectedSpawns)
    },
    movesByMonsterID () {
      const grouped = Object.create(null)
      ;(this.spawnMoves || []).forEach(move => {
        const monsterID = Number(move.monsterID || 0)
        if (!monsterID || Number(move.level || 0) <= 0) return
        if (!grouped[monsterID]) grouped[monsterID] = []
        grouped[monsterID].push({
          id: Number(move.id || 0),
          monsterID,
          level: Number(move.level || 0),
          skillID: Number(move.skillID || 0),
          name: move.name || ('#' + move.skillID)
        })
      })
      Object.keys(grouped).forEach(monsterID => {
        grouped[monsterID].sort((a, b) => {
          const levelCompare = a.level - b.level
          if (levelCompare !== 0) return levelCompare
          return a.id - b.id
        })
      })
      return grouped
    },
    selfHarmingMoveSet () {
      const moves = this.referenceData && this.referenceData.selfHarmingMoves ? this.referenceData.selfHarmingMoves : []
      return moves.reduce((set, move) => {
        const skillID = Number(move || 0)
        if (skillID > 0) {
          set[skillID] = true
        }
        return set
      }, Object.create(null))
    },
    selfHarmingMoveSignature () {
      return Object.keys(this.selfHarmingMoveSet).sort((a, b) => Number(a) - Number(b)).join(',')
    },
    preferredNaturesByDexID () {
      const source = this.referenceData && this.referenceData.preferredPokemonNatures ? this.referenceData.preferredPokemonNatures : {}
      return Object.keys(source).reduce((result, dexID) => {
        const key = String(Number(dexID || 0))
        if (key === '0') return result
        result[key] = (source[dexID] || []).map(nature => String(nature || '').trim()).filter(nature => nature)
        return result
      }, Object.create(null))
    },
    referenceTierListByDexID () {
      const source = this.referenceData && this.referenceData.pokemonRarityTiers ? this.referenceData.pokemonRarityTiers : {}
      return Object.keys(source).reduce((result, dexID) => {
        const key = String(Number(dexID || 0))
        if (key === '0') return result
        const tiers = Array.isArray(source[dexID]) ? source[dexID] : [source[dexID]]
        result[key] = tiers.map(tier => String(tier || '').trim()).filter(tier => tier)
        return result
      }, Object.create(null))
    },
    habitatDexIDSets () {
      const habitats = this.referenceData && this.referenceData.habitats ? this.referenceData.habitats : {}
      return Object.keys(habitats).reduce((result, habitat) => {
        result[habitat] = (habitats[habitat] || []).reduce((set, value) => {
          const dexID = Number(value || 0)
          if (dexID) set[dexID] = true
          return set
        }, Object.create(null))
        return result
      }, Object.create(null))
    },
    eventNamesByDexID () {
      const mappings = this.formMapping && this.formMapping.mappings ? this.formMapping.mappings : {}
      return Object.keys(mappings).reduce((result, dexID) => {
        const key = String(Number(dexID || 0))
        if (key === '0') return result
        const names = Object.create(null)
        ;(mappings[dexID] || []).forEach(form => {
          const name = String(form.name || '').trim()
          if (name && !this.isShinyFormName(name)) names[name] = true
        })
        result[key] = names
        return result
      }, Object.create(null))
    },
    usageRanksByDexID () {
      const result = Object.create(null)
      ;(this.spawnUsageRanks || []).forEach(rank => {
        if (!rank || Number(rank.rank || 0) < 1 || Number(rank.rank || 0) > 100) return
        const lineDexIDs = Array.isArray(rank.lineDexIDs) && rank.lineDexIDs.length ? rank.lineDexIDs : [rank.dexID]
        lineDexIDs.forEach(dexID => {
          const numericDex = Number(dexID || 0)
          if (!numericDex) return
          if (!result[numericDex]) result[numericDex] = []
          result[numericDex].push(rank)
        })
      })
      Object.keys(result).forEach(dexID => {
        result[dexID].sort((a, b) => Number(a.rank || 0) - Number(b.rank || 0))
      })
      return result
    },
    pokemonCatalogOptions () {
      const source = (this.spawnPokemonCatalog && this.spawnPokemonCatalog.length ? this.spawnPokemonCatalog : Monsters.map(monster => ({
        dexID: Number(monster.ID || 0),
        name: monster.Name || ('#' + monster.ID),
        type1: '',
        type2: '',
        evolutionStage: '',
        legendary: false
      })))
      return source
        .map(entry => {
          const dexID = Number(entry.dexID || entry.ID || 0)
          const name = entry.name || entry.Name || ('#' + dexID)
          const type1 = String(entry.type1 || '').trim()
          const type2 = String(entry.type2 || '').trim()
          return {
            dexID,
            name,
            searchText: searchText(name) + ' #' + dexID,
            type1,
            type2,
            typeSearch: [type1, type2].map(type => searchText(type)).filter(type => type),
            evolutionStage: String(entry.evolutionStage || '').trim(),
            legendary: !!entry.legendary
          }
        })
        .filter(entry => entry.dexID > 0 && entry.name)
    },
    pokemonCatalogByDexID () {
      return this.pokemonCatalogOptions.reduce((result, option) => {
        result[String(option.dexID)] = option
        return result
      }, Object.create(null))
    },
    addSpawnSortOptions () {
      return ADD_SPAWN_SORT_OPTIONS
    },
    rarityTierOptions () {
      return ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T9.5', 'T10']
        .map(tier => ({ value: tier, text: this.tierDisplayLabel(tier) }))
    },
    addSpawnFilterGroups () {
      return [
        { key: 'types', label: 'Type', options: this.addSpawnTypeOptions },
        { key: 'rarities', label: 'Rarity', options: this.addSpawnRarityOptions },
        { key: 'habitats', label: 'Habitat', options: this.addSpawnHabitatOptions },
        { key: 'evolutionStages', label: 'Evolution', options: this.addSpawnEvolutionOptions },
        { key: 'regions', label: 'Region', options: this.addSpawnRegionOptions },
        { key: 'events', label: 'Event Forms', options: this.addSpawnEventOptions },
        { key: 'natures', label: 'Preferred Natures', options: this.addSpawnNatureOptions },
        { key: 'topUsage', label: 'Top 100 PvP Usage', options: [] },
        { key: 'hideAdded', label: 'Hide added Pokémon', options: [] }
      ]
    },
    addSpawnTypeOptions () {
      const seen = Object.create(null)
      this.pokemonCatalogOptions.forEach(option => {
        ;[option.type1, option.type2].forEach(type => {
          const value = String(type || '').trim()
          if (value) seen[value] = true
        })
      })
      return Object.keys(seen).sort().map(type => ({ value: type, text: type }))
    },
    addSpawnRarityOptions () {
      const tiers = this.referenceData && this.referenceData.pokemonRarityTiers ? this.referenceData.pokemonRarityTiers : {}
      const seen = Object.create(null)
      Object.keys(tiers).forEach(dexID => {
        ;(Array.isArray(tiers[dexID]) ? tiers[dexID] : [tiers[dexID]]).forEach(tier => {
          const value = String(tier || '').trim()
          if (value) seen[value] = true
        })
      })
      return Object.keys(seen)
        .sort((a, b) => this.raritySortValueForTier(a) - this.raritySortValueForTier(b))
        .map(tier => ({ value: tier, text: this.tierDisplayLabel(tier) }))
    },
    itemOptions () {
      const items = this.referenceLookups && this.referenceLookups.items ? this.referenceLookups.items : {}
      return Object.keys(items)
        .map(id => ({
          id: Number(id || 0),
          name: String(items[id] || '').trim()
        }))
        .filter(item => item.id > 0 && item.name)
        .sort((a, b) => SPAWN_NAME_COLLATOR.compare(a.name, b.name))
    },
    itemLookupOptions () {
      return this.itemOptions.map(item => ({
        id: item.id,
        label: item.name,
        search: item.name + ' #' + item.id
      }))
    },
    itemNameByID () {
      return this.itemOptions.reduce((result, item) => {
        result[String(item.id)] = item.name
        return result
      }, Object.create(null))
    },
    itemIDByNormalizedName () {
      return this.itemOptions.reduce((result, item) => {
        const key = this.normalizeLookupText(item.name)
        if (key && !result[key]) result[key] = item.id
        return result
      }, Object.create(null))
    },
    addSpawnHabitatOptions () {
      const habitats = this.referenceData && this.referenceData.habitats ? this.referenceData.habitats : {}
      return Object.keys(habitats).sort().map(habitat => ({ value: habitat, text: habitat }))
    },
    addSpawnNatureOptions () {
      const natures = Object.create(null)
      const source = this.referenceData && this.referenceData.preferredPokemonNatures ? this.referenceData.preferredPokemonNatures : {}
      Object.keys(source).forEach(dexID => {
        ;(source[dexID] || []).forEach(nature => {
          const value = String(nature || '').trim()
          if (value) natures[value] = true
        })
      })
      return Object.keys(natures).sort().map(nature => ({ value: nature, text: nature }))
    },
    addSpawnEventOptions () {
      const names = this.formMapping && this.formMapping.names ? this.formMapping.names : {}
      const seen = Object.create(null)
      Object.keys(names).forEach(key => {
        const name = String(names[key] || '').trim()
        if (this.isShinyFormName(name)) return
        if (name) seen[name] = true
      })
      return Object.keys(seen).sort().map(name => ({ value: name, text: name }))
    },
    addSpawnRegionOptions () {
      return REGION_FILTERS.map(region => ({ value: region.value, text: region.text }))
    },
    addSpawnEvolutionOptions () {
      return EVOLUTION_STAGE_OPTIONS
    },
    filteredAddSpawnPokemon () {
      const search = searchText(this.addSpawnModal.search).trim()
      const filters = this.addSpawnModal.filters
      const activeFilters = this.addSpawnModal.activeFilters || {}
      const typeFilters = activeFilters.types && filters.types.length ? filters.types.map(type => searchText(type)) : []
      const rows = this.pokemonCatalogOptions
        .filter(option => {
          if (this.isExcludedAddSpawnPokemon(option)) return false
          if (search && option.searchText.indexOf(search) === -1) return false
          if (typeFilters.length && !typeFilters.some(type => this.pokemonOptionTypes(option).indexOf(type) !== -1)) return false
          if (activeFilters.rarities && filters.rarities.length && !filters.rarities.some(tier => this.referenceTiersForPokemon(option.dexID).indexOf(tier) !== -1)) return false
          if (activeFilters.habitats && filters.habitats.length && !filters.habitats.some(habitat => this.pokemonInHabitat(option.dexID, habitat))) return false
          if (activeFilters.evolutionStages && filters.evolutionStages.length && filters.evolutionStages.indexOf(option.evolutionStage) === -1) return false
          if (activeFilters.regions && filters.regions.length && !filters.regions.some(region => this.pokemonInRegion(option.dexID, region))) return false
          if (activeFilters.events && filters.events.length && !filters.events.some(event => this.pokemonHasEventCategory(option.dexID, event))) return false
          if (activeFilters.natures && filters.natures.length && !filters.natures.some(nature => this.naturesForDexID(option.dexID).indexOf(nature) !== -1)) return false
          if (activeFilters.topUsage && !this.usageRanksByDexID[Number(option.dexID || 0)]) return false
          return true
        })
      return rows.sort((a, b) => {
        const direction = this.addSpawnModal.sortDirection === 'desc' ? -1 : 1
        if (this.addSpawnModal.sortKey === 'name') {
          return direction * SPAWN_NAME_COLLATOR.compare(a.name, b.name)
        }
        if (this.addSpawnModal.sortKey === 'rarity') {
          const rarityCompare = this.addSpawnRaritySortValue(a) - this.addSpawnRaritySortValue(b)
          if (rarityCompare !== 0) return direction * rarityCompare
          return direction * SPAWN_NAME_COLLATOR.compare(a.name, b.name)
        }
        return direction * (Number(a.dexID || 0) - Number(b.dexID || 0))
      })
    },
    availableAddSpawnPokemon () {
      if (!this.hideAddedAddSpawnPokemon) return this.filteredAddSpawnPokemon
      return this.filteredAddSpawnPokemon.filter(pokemon => {
        const key = String(pokemon.dexID)
        if (this.activeAddSpawnSelectedDexIDs[key]) return false
        return !this.activeAddSpawnExistingDexIDs[key]
      })
    },
    hideAddedAddSpawnPokemon () {
      return this.isAddSpawnFilterActive('hideAdded')
    },
    activeAddSpawnSelectedDexIDs () {
      return this.selectedAddSpawnPokemon.reduce((result, pokemon) => {
        result[String(Number(pokemon.dexID || 0))] = true
        return result
      }, Object.create(null))
    },
    selectedAddSpawnPokemon () {
      const selected = this.addSpawnModal.selected || {}
      const ordered = (this.addSpawnModal.selectedOrder || []).filter(dexID => selected[String(dexID)])
      Object.keys(selected).forEach(dexID => {
        if (selected[dexID] && ordered.indexOf(dexID) === -1) ordered.push(dexID)
      })
      return ordered
        .map(selectionKey => {
          const entry = selected[String(selectionKey)]
          const dexID = Number(entry && entry.dexID !== undefined ? entry.dexID : entry)
          const pokemon = this.pokemonCatalogByDexID[String(dexID)]
          if (!pokemon) return null
          return Object.assign({}, pokemon, {
            selectionKey: String(selectionKey),
            addSpawnTimeKeys: this.addSpawnSelectedTimeKeys(selectionKey)
          })
        })
        .filter(pokemon => pokemon)
    },
    selectedAddSpawnCount () {
      return this.selectedAddSpawnPokemon.length
    },
    removedExistingSpawnCount () {
      const removed = this.addSpawnModal.removedExisting || {}
      const explicit = Object.keys(removed).filter(key => removed[key]).length
      return explicit + this.currentAddSpawnExistingRows.filter(spawn => !removed[this.spawnKey(spawn)] && this.isAddSpawnExistingAutoRemoved(spawn)).length
    },
    draftLevelMinInputValue () {
      return this.draftLevelMinInput
    },
    draftLevelMaxInputValue () {
      return this.draftLevelMaxInput
    },
    draftLevelMinInputState () {
      return this.levelRangeInputState(this.draftLevelMinInput, this.draftLevelMaxInput, 'min')
    },
    draftLevelMaxInputState () {
      return this.levelRangeInputState(this.draftLevelMinInput, this.draftLevelMaxInput, 'max')
    },
    canApplyAddSpawnChanges () {
      return this.selectedAddSpawnCount > 0 || this.removedExistingSpawnCount > 0 || this.hasAddSpawnExistingTimeChanges
    },
    addSpawnOkDisabled () {
      return !this.canApplyAddSpawnChanges || (this.selectedAddSpawnCount > 0 && !this.canUseAddSpawnLevelRange)
    },
    addSpawnOkTitle () {
      if (this.selectedAddSpawnCount > 0) return 'Add Selected'
      if (this.removedExistingSpawnCount > 0) return 'Apply Changes'
      return 'Add Selected'
    },
    canUseAddSpawnLevelRange () {
      return !!this.normalizedDraftLevelRange()
    },
    canAddSpawnInEdit () {
      return !!this.editMode
    },
    originalEditRows () {
      if (this.workflowView === 'live') return this.liveSpawnsForMapListName(this.activeMap)
      if (this.activeBatch) return this.activeBatch.spawns || []
      return []
    },
    draftSignature () {
      return this.spawnRowsSignature(this.editRows)
    },
    originalDraftSignature () {
      return this.spawnRowsSignature(this.originalEditRows)
    },
    hasDraftChanges () {
      return this.draftSignature !== this.originalDraftSignature
    },
    hasEditLevelInputErrors () {
      return this.editRows.some(row => !this.validLevelRangeForSpawn(row))
    },
    activeComparisonRows () {
      return this.comparisonRowsForBatch(this.activeBatch)
    },
    activeBatchHasChanges () {
      return this.activeComparisonRows.length > 0
    },
    isSavedWorkspaceDraft () {
      return this.workflowView === 'wip' && !!this.activeBatch
    },
    saveDraftLabel () {
      return this.isSavedWorkspaceDraft ? 'Save' : 'Save to Workspace'
    },
    saveDraftDisabled () {
      if (this.hasEditLevelInputErrors) return true
      return this.isSavedWorkspaceDraft && !this.hasDraftChanges
    },
    discardDraftDisabled () {
      return !this.hasDraftChanges && !this.hasEditLevelInputErrors
    },
    editModeStatusText () {
      if (!this.isSavedWorkspaceDraft) return 'This map is not saved in Workspace yet.'
      return this.hasDraftChanges ? 'Unsaved changes in Workspace.' : 'No changes.'
    },
    currentAddSpawnExistingRows () {
      if (!this.addSpawnModal.visible) return []
      const water = this.addSpawnModal.area === 'water' || this.addSpawnModal.area === 'surf'
      return this.editRows
        .filter(spawn => this.isWaterSpawn(spawn) === water)
        .slice()
        .sort(this.compareSpawns)
    },
    hasAddSpawnExistingTimeChanges () {
      const overrides = this.addSpawnModal.existingTimeOverrides || {}
      return Object.keys(overrides).some(key => {
        const spawn = this.editRows.find(row => this.spawnKey(row) === key)
        if (!spawn) return false
        return this.timeKeysSignature(overrides[key]) !== this.timeKeysSignature(this.activeTimeKeys(spawn))
      })
    },
    activeAddSpawnExistingDexIDs () {
      return this.currentAddSpawnExistingRows.reduce((result, spawn) => {
        if (!this.isAddSpawnExistingRemovalPending(spawn)) {
          result[String(Number(spawn.dexID || 0))] = true
        }
        return result
      }, Object.create(null))
    },
    activeAddSpawnExistingCount () {
      return this.currentAddSpawnExistingRows.filter(spawn => !this.isAddSpawnExistingRemovalPending(spawn)).length
    },
    addSpawnPreviewRows () {
      const removed = this.addSpawnModal.removedExisting || {}
      const rows = this.editRows
        .map(spawn => this.cloneSpawn(spawn))
        .filter(spawn => !removed[this.spawnKey(spawn)])
        .map(spawn => this.applyAddSpawnExistingTimeOverride(spawn))
        .filter(spawn => this.activeTimeKeys(spawn).length > 0)
      this.selectedAddSpawnPokemon.forEach(pokemon => {
        rows.push(this.createSpawnForPokemon(pokemon, this.addSpawnModal.area, true))
      })
      return rows
    },
    landNatureStats () {
      return this.natureStatsForSpawns(this.landNatureSpawns)
    },
    waterNatureStats () {
      return this.natureStatsForSpawns(this.waterNatureSpawns)
    },
    selfHarmingWarnings () {
      if (!this.showWarnings || !this.mapAnalysisReady) return []
      const seen = Object.create(null)
      const warnings = []
      this.visibleSelectedSpawns.forEach(spawn => {
        this.possibleMoves(spawn).forEach(move => {
          if (!this.isSelfHarmingMove(move, spawn)) return
          const levelRange = move.rangeText || (spawn.minLVL + '-' + spawn.maxLVL)
          const key = this.pokemonName(spawn) + '-' + move.name + '-' + levelRange
          if (seen[key]) return
          seen[key] = true
          warnings.push({
            key,
            text: this.pokemonName(spawn) + ' can know ' + move.name + ' at Lv. ' + levelRange + '.'
          })
        })
      })
      return warnings.sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true }))
    },
    selfHarmingWarningBySpawnKey () {
      if (!this.showWarnings || !this.mapAnalysisReady) return Object.create(null)
      const result = Object.create(null)
      this.visibleSelectedSpawns.forEach(spawn => {
        if (this.spawnHasSelfHarmingMoves(spawn)) {
          result[this.spawnKey(spawn)] = true
        }
      })
      return result
    },
    repelWarnings () {
      if (!this.showWarnings || !this.mapAnalysisReady) return []
      const seen = Object.create(null)
      const entries = Object.values(this.repelInfoBySpawnKey).filter(entry => {
        const key = entry.dexID + '-' + entry.minLevel + '-' + entry.reason + '-' + entry.periods.map(period => period.key).join('-')
        if (seen[key]) return false
        seen[key] = true
        return true
      })
      return entries
        .map(entry => ({
          key: 'repel-' + entry.dexID + '-' + entry.minLevel + '-' + entry.reason,
          text: entry.name + ' can ' + (entry.reason === 'solo' ? 'be isolated' : 'leave only T7+ Pokemon') + ' with Repel from Lv. ' + entry.minLevel + ' during ' + this.formatPeriodLabels(entry.periods) + '.'
        }))
        .sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true }))
    },
    repelInfoBySpawnKey () {
      if (!this.showWarnings || !this.mapAnalysisReady) return Object.create(null)
      return this.buildRepelInfoBySpawnKey(this.selectedSpawns)
    },
    landNatureConflictWarnings () {
      if (!this.showWarnings || !this.mapAnalysisReady) return []
      return this.buildNatureConflictWarnings(this.landNatureAnalysisSpawns, 'land')
    },
    waterNatureConflictWarnings () {
      if (!this.showWarnings || !this.mapAnalysisReady) return []
      return this.buildNatureConflictWarnings(this.waterNatureAnalysisSpawns, 'water')
    },
    natureConflictWarnings () {
      return this.landNatureConflictWarnings.concat(this.waterNatureConflictWarnings)
    },
    natureConflictBySpawnKey () {
      if (!this.showWarnings || !this.mapAnalysisReady) return Object.create(null)
      const spawnKeys = Object.create(null)
      this.natureConflictWarnings.forEach(warning => {
        warning.spawnKeys.forEach(key => {
          spawnKeys[key] = true
        })
      })
      const result = Object.create(null)
      this.selectedSpawns.forEach(spawn => {
        if (spawnKeys[this.spawnKey(spawn)]) {
          result[this.spawnKey(spawn)] = true
        }
      })
      return result
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.resetMapSelection()
    })
  },
  watch: {
    workflowView (view) {
      this.discardEdit()
      this.activeBatchId = null
      this.activeMap = ''
      this.clearMapSearchValue()
      this.selectedSearchEntity = null
      if (view === 'live') {
        this.workflowStackedMaps = false
      }
      this.$nextTick(() => {
        this.applyPendingWorkflowSelection(view)
      })
    },
    lastDraftSave (saved) {
      this.handleSpawnDraftSaved(saved)
    },
    activeMap () {
      this.refreshMapImageState()
      this.resetMainRenderBudget()
    },
    activeBatchId () {
      this.resetMainRenderBudget()
    },
    displayMode () {
      this.resetMainRenderBudget()
    },
    activeTimeFilter () {
      this.resetMainRenderBudget()
    },
    editMode () {
      this.resetMainRenderBudget()
      this.scheduleSidebarScrollSync()
    },
    workflowStackedMaps () {
      this.resetMainRenderBudget()
    },
    loading () {
      this.$nextTick(this.syncSidebarScrollBounds)
    },
    mapSearch () {
      if (this.mapSearchTimer) {
        window.clearTimeout(this.mapSearchTimer)
        this.mapSearchTimer = null
      }
      const next = String(this.mapSearch || '')
      this.pendingMapSearchInput = next
      if (this.mapSearchInput !== next) {
        this.mapSearchInput = next
      }
      this.syncMapSearchInputElement(next)
      this.resetSidebarScroll()
    },
    mapSearchMode () {
      this.clearMapSearchValue()
      this.selectedSearchEntity = null
      this.resetSidebarScroll()
    },
    selectedSearchEntity () {
      this.resetSidebarScroll()
    },
    sidebarItems () {
      this.$nextTick(this.syncSidebarScrollBounds)
    },
    maps: {
      immediate: true,
      handler (maps) {
        if (!maps.length) {
          this.activeMap = ''
          return
        }
        if (!this.activeMap || !maps.some(map => map.name === this.activeMap)) {
          this.activeMap = maps[0].name
          this.activeBatchId = maps[0].batchID || null
        }
      }
    },
    spawnsUpdatedAt () {
      this.loading = false
      if (this.loadingTimer) {
        window.clearTimeout(this.loadingTimer)
        this.loadingTimer = null
      }
      this.clearPokemonMapItemsCache()
      this.clearRepelCache()
      this.resetMainRenderBudget()
    },
    workflowUpdatedAt () {
      if (this.pendingNewMapName) {
        const created = (this.workflowBatches || []).find(batch => batch.status === 'wip' && batch.isOwner && batch.mapName === this.pendingNewMapName)
        if (created) {
          this.newMapModal.visible = false
          this.newMapModal.mapName = ''
          this.pendingNewMapName = ''
          this.selectWorkflowBatch(created)
        }
      }
      if (this.workflowView !== 'live' && this.activeBatchId && !this.currentWorkflowBatches.some(batch => Number(batch.id) === Number(this.activeBatchId))) {
        this.activeBatchId = null
      }
      if (!this.isWorkflowViewVisible(this.workflowView)) {
        this.workflowView = 'wip'
      }
      if (this.workflowStackedMaps && !this.workflowStackAvailable) {
        this.workflowStackedMaps = false
      }
      this.finishPendingDraftSave()
      this.clearPokemonMapItemsCache()
      this.clearRepelCache()
      this.resetMainRenderBudget()
    },
    spawnMoves () {
      this.clearMoveAnalysisCache()
    },
    referenceDataUpdatedAt () {
      this.referenceAnalysisRequestPending = false
      this.clearPokemonMapItemsCache()
      this.clearMoveAnalysisCache()
    },
    sortKey () {
      this.resetMainRenderBudget()
      this.savePreferences()
    },
    sortDirection () {
      this.resetMainRenderBudget()
      this.savePreferences()
    },
    membershipActive () {
      this.clearRepelCache()
      this.resetMainRenderBudget()
      this.savePreferences()
    },
    showWarnings () {
      if (this.showWarnings) {
        this.scheduleSpawnReferenceAnalysis()
      } else {
        this.cancelScheduledSpawnReferenceAnalysis()
      }
      this.clearRepelCache()
      this.resetMainRenderBudget()
      this.savePreferences()
    }
  },
  created () {
    this.loadPreferences()
    this.refreshSpawns()
    this.emit('getSpawnEditorWorkflow', {})
    this.requestSpawnReferenceData(true)
    if (this.showWarnings) {
      this.scheduleSpawnReferenceAnalysis()
    }
    this.emit('getFormMapping', '')
  },
  mounted () {
    this.$nextTick(this.updateSidebarViewport)
    this.$nextTick(this.observeSidebarList)
    this.$nextTick(this.resetMainRenderBudget)
    window.addEventListener('resize', this.updateSidebarViewport)
  },
  beforeDestroy () {
    if (this.loadingTimer) {
      window.clearTimeout(this.loadingTimer)
    }
    if (this.mapSearchTimer) {
      window.clearTimeout(this.mapSearchTimer)
    }
    this.cancelMainRenderPump()
    this.cancelMapSwitchFinish()
    this.cancelMapAnalysis()
    this.cancelScheduledSpawnReferenceAnalysis()
    if (this.sidebarResizeObserver) {
      this.sidebarResizeObserver.disconnect()
      this.sidebarResizeObserver = null
    }
    window.removeEventListener('resize', this.updateSidebarViewport)
  },
  methods: {
    requestSpawnReferenceData (referenceOnly) {
      const slim = !!referenceOnly
      if (!slim) {
        this.referenceAnalysisRequestPending = true
      }
      this.emit('getSpawnReferenceData', { referenceOnly: slim })
    },
    ensureSpawnReferenceAnalysis () {
      if (this.spawnReferenceAnalysisLoaded || this.referenceAnalysisRequestPending) return
      this.cancelScheduledSpawnReferenceAnalysis()
      this.requestSpawnReferenceData(false)
    },
    scheduleSpawnReferenceAnalysis () {
      if (this.spawnReferenceAnalysisLoaded || this.referenceAnalysisRequestPending || this.referenceAnalysisFrame) return
      if (typeof window === 'undefined') {
        this.ensureSpawnReferenceAnalysis()
        return
      }
      if (window.requestIdleCallback) {
        this.referenceAnalysisFrame = window.requestIdleCallback(() => {
          this.referenceAnalysisFrame = null
          this.ensureSpawnReferenceAnalysis()
        }, { timeout: 2000 })
        return
      }
      this.referenceAnalysisFrame = window.setTimeout(() => {
        this.referenceAnalysisFrame = null
        this.ensureSpawnReferenceAnalysis()
      }, 250)
    },
    cancelScheduledSpawnReferenceAnalysis () {
      if (!this.referenceAnalysisFrame || typeof window === 'undefined') {
        this.referenceAnalysisFrame = null
        return
      }
      if (window.cancelIdleCallback) {
        window.cancelIdleCallback(this.referenceAnalysisFrame)
      } else {
        window.clearTimeout(this.referenceAnalysisFrame)
      }
      this.referenceAnalysisFrame = null
    },
    clearPokemonMapItemsCache () {
      this.pokemonMapItemsCache = Object.create(null)
    },
    sidebarItemHeight (item) {
      if (item && (item.type === 'pokemonMap' || item.type === 'regionMap')) return SIDEBAR_STACKED_ROW_HEIGHT
      return SIDEBAR_ROW_HEIGHT
    },
    handleSidebarScroll (event) {
      this.sidebarScrollTop = event && event.target ? event.target.scrollTop : 0
    },
    spawnsForMapListName (mapName) {
      return this.spawnsByMapListKey[mapListKey(mapName)] || this.spawnsByMap[mapName] || []
    },
    liveSpawnsForMapListName (mapName) {
      return this.liveSpawnsByMapListKey[mapListKey(mapName)] || this.liveSpawnsByMap[mapName] || []
    },
    sidebarSpawnsForMapListName (mapName) {
      if (this.workflowView === 'live') return this.spawnsForMapListName(mapName)
      const key = mapListKey(mapName)
      return (this.currentWorkflowBatches || [])
        .filter(batch => mapListKey(batch.mapName) === key)
        .reduce((rows, batch) => rows.concat(batch.spawns || []), [])
    },
    updateSidebarViewport () {
      const list = this.$refs.sidebarList
      if (!list) return
      this.sidebarViewportHeight = list.clientHeight || 0
    },
    observeSidebarList () {
      const list = this.$refs.sidebarList
      if (!list) return
      if (this.sidebarResizeObserver) {
        this.sidebarResizeObserver.disconnect()
        this.sidebarResizeObserver = null
      }
      if (typeof window === 'undefined' || typeof window.ResizeObserver === 'undefined') {
        this.updateSidebarViewport()
        return
      }
      this.sidebarResizeObserver = new window.ResizeObserver(() => {
        this.syncSidebarScrollBounds()
      })
      this.sidebarResizeObserver.observe(list)
      this.updateSidebarViewport()
    },
    scheduleSidebarScrollSync () {
      this.$nextTick(() => {
        this.syncSidebarScrollBounds()
        if (typeof window === 'undefined' || !window.requestAnimationFrame) return
        window.requestAnimationFrame(() => {
          this.syncSidebarScrollBounds()
        })
      })
    },
    syncSidebarScrollBounds () {
      this.updateSidebarViewport()
      const list = this.$refs.sidebarList
      const maxScrollTop = list
        ? Math.max(0, list.scrollHeight - list.clientHeight)
        : Math.max(0, this.virtualSidebarTotalHeight - (this.sidebarViewportHeight || 0))
      if (this.sidebarScrollTop <= maxScrollTop) return
      this.sidebarScrollTop = maxScrollTop
      if (list) {
        list.scrollTop = maxScrollTop
      }
    },
    resetSidebarScroll () {
      this.sidebarScrollTop = 0
      this.$nextTick(() => {
        const list = this.$refs.sidebarList
        if (list) {
          list.scrollTop = 0
        }
        this.updateSidebarViewport()
      })
    },
    handleMapSearchInput (value) {
      this.pendingMapSearchInput = String(value || '')
      if (this.mapSearchTimer) {
        window.clearTimeout(this.mapSearchTimer)
      }
      this.mapSearchTimer = window.setTimeout(() => {
        this.mapSearchTimer = null
        this.commitMapSearchInput(this.pendingMapSearchInput)
      }, SPAWN_SEARCH_DEBOUNCE_MS)
    },
    commitMapSearchInput (value) {
      const next = String(value || '')
      this.pendingMapSearchInput = next
      if (this.mapSearchInput !== next) {
        this.mapSearchInput = next
      }
      if (this.mapSearch !== next) {
        this.mapSearch = next
      } else {
        this.syncMapSearchInputElement(next)
      }
    },
    clearMapSearchValue () {
      if (this.mapSearchTimer) {
        window.clearTimeout(this.mapSearchTimer)
        this.mapSearchTimer = null
      }
      this.commitMapSearchInput('')
    },
    syncMapSearchInputElement (value) {
      this.$nextTick(() => {
        const ref = this.$refs.mapSearchInput
        const input = ref && ref.$el ? ref.$el : ref
        if (input && input.value !== undefined && input.value !== value) {
          input.value = value
        }
      })
    },
    resetMainRenderBudget () {
      this.cancelMainRenderPump()
      this.deferMapAnalysis()
      this.mainRenderCounts = this.initialMainRenderCounts()
      this.$nextTick(() => {
        this.scheduleMainRenderPump()
        this.scheduleMapSwitchFinish()
      })
    },
    initialMainRenderCounts () {
      const counts = { land: 0, water: 0 }
      if (this.renderAllSpawnCards) return counts
      if (this.showLand) {
        counts.land = SPAWN_RENDER_INITIAL_COUNT
      }
      if (this.showWater) {
        counts.water = SPAWN_RENDER_INITIAL_COUNT
      }
      return counts
    },
    cancelMainRenderPump () {
      if (this.mainRenderFrame && typeof window !== 'undefined') {
        window.cancelAnimationFrame(this.mainRenderFrame)
      }
      this.mainRenderFrame = null
    },
    beginMapSwitchLoading () {
      if (this.workflowStackedMaps || typeof window === 'undefined') return
      this.cancelMapSwitchFinish()
      this.mapSwitchStartedAt = Date.now()
      this.mapSwitchLoading = true
    },
    cancelMapSwitchFinish () {
      if (this.mapSwitchTimer && typeof window !== 'undefined') {
        window.clearTimeout(this.mapSwitchTimer)
      }
      if (this.mapSwitchFrame && typeof window !== 'undefined') {
        window.cancelAnimationFrame(this.mapSwitchFrame)
      }
      this.mapSwitchTimer = null
      this.mapSwitchFrame = null
    },
    scheduleMapSwitchFinish () {
      if (!this.mapSwitchLoading || !this.mainRenderPrimed || typeof window === 'undefined') return
      if (this.mapSwitchTimer || this.mapSwitchFrame) return
      this.cancelMapSwitchFinish()
      const elapsed = Date.now() - this.mapSwitchStartedAt
      const delay = Math.max(0, MAP_SWITCH_LOADING_MIN_MS - elapsed)
      const finish = () => {
        this.mapSwitchFrame = window.requestAnimationFrame(() => {
          this.mapSwitchFrame = null
          this.mapSwitchLoading = false
        })
      }
      if (delay > 0) {
        this.mapSwitchTimer = window.setTimeout(() => {
          this.mapSwitchTimer = null
          finish()
        }, delay)
        return
      }
      finish()
    },
    deferMapAnalysis () {
      this.cancelMapAnalysis()
      this.mapAnalysisReady = true
    },
    cancelMapAnalysis () {
      if (!this.mapAnalysisFrame || typeof window === 'undefined') return
      if (window.cancelIdleCallback) {
        window.cancelIdleCallback(this.mapAnalysisFrame)
      } else {
        window.cancelAnimationFrame(this.mapAnalysisFrame)
      }
      this.mapAnalysisFrame = null
    },
    scheduleMainRenderPump () {
      if (this.mainRenderComplete || this.mainRenderFrame || typeof window === 'undefined') {
        this.scheduleMapSwitchFinish()
        return
      }
      this.mainRenderFrame = window.requestAnimationFrame(() => {
        this.mainRenderFrame = null
        this.expandMainRenderBudget()
        this.scheduleMainRenderPump()
      })
    },
    expandMainRenderBudget () {
      if (this.mainRenderComplete) return
      const counts = Object.assign({}, this.mainRenderCounts)
      if (this.showLand && counts.land < this.landSpawns.length) {
        counts.land = Math.min(this.landSpawns.length, counts.land + SPAWN_RENDER_CHUNK_SIZE)
      }
      if (this.showWater && counts.water < this.waterSpawns.length) {
        counts.water = Math.min(this.waterSpawns.length, counts.water + SPAWN_RENDER_CHUNK_SIZE)
      }
      this.mainRenderCounts = counts
      this.$nextTick(this.scheduleMapSwitchFinish)
    },
    handleMapImageLoad () {
      this.mapImageError = false
      this.mapImageLoadedUrl = this.activeMapImageUrl
    },
    handleMapImageError () {
      this.mapImageError = true
      this.mapImageLoadedUrl = ''
    },
    refreshMapImageState () {
      this.mapImageError = false
      this.mapImageLoadedUrl = ''
      this.syncMapImageLoadState()
    },
    syncMapImageLoadState () {
      this.$nextTick(() => {
        const image = this.$refs.mapStageImage
        if (!this.activeMapImageUrl || !image || !image.complete) return
        if (image.naturalWidth > 0) {
          this.handleMapImageLoad()
        } else {
          this.handleMapImageError()
        }
      })
    },
    refreshSpawns () {
      this.loading = true
      this.emit('getSpawns', {})
      this.emit('getSpawnEditorWorkflow', {})
      if (this.loadingTimer) {
        window.clearTimeout(this.loadingTimer)
      }
      this.loadingTimer = window.setTimeout(() => {
        this.loading = false
        this.loadingTimer = null
      }, 15000)
    },
    setWorkflowView (view) {
      if (view === this.workflowView) return
      if (!this.isWorkflowViewVisible(view)) return
      if (this.editMode && !this.confirmDiscardEdit()) return
      this.pendingWorkflowSelection = null
      this.discardEdit()
      this.workflowView = view
    },
    showSpawnSectionActions (area) {
      if (this.mapSpawnsLoading) return false
      if (this.editMode) return false
      if (!this.editMode && !this.canBeginEdit) return false
      if (area === 'land') return this.showLand
      return !this.showLand && this.showWater
    },
    rarityValueForTier (tier, water) {
      const table = water ? WATER_RARITIES : LAND_RARITIES
      const match = table.find(entry => entry.tier === tier)
      return match ? match.value : Number(tier || 0) || 0
    },
    areaLabel (spawn) {
      return this.isWaterSpawn(spawn) ? 'Water' : 'Land'
    },
    pokemonOptionTypes (option) {
      return option.typeSearch || []
    },
    referenceTiersForPokemon (dexID) {
      return this.referenceTierListByDexID[String(Number(dexID || 0))] || []
    },
    pokemonInHabitat (dexID, habitat) {
      const set = this.habitatDexIDSets[habitat] || Object.create(null)
      return !!set[Number(dexID || 0)]
    },
    pokemonInRegion (dexID, regionKey) {
      const region = REGION_FILTERS.find(candidate => candidate.value === regionKey)
      const numericDexID = Number(dexID || 0)
      return !!region && region.ranges.some(range => numericDexID >= range[0] && numericDexID <= range[1])
    },
    pokemonHasEventCategory (dexID, eventName) {
      const names = this.eventNamesByDexID[String(Number(dexID || 0))] || Object.create(null)
      return !!names[eventName]
    },
    naturesForDexID (dexID) {
      return this.preferredNaturesByDexID[String(Number(dexID || 0))] || []
    },
    isShinyFormName (name) {
      return String(name || '').toLowerCase().indexOf('shiny') !== -1
    },
    isExcludedAddSpawnPokemon (option) {
      const name = String(option && option.name ? option.name : '').trim()
      if (name.indexOf('Mega ') === 0 || name.indexOf('Gigantamax ') === 0) return true
      return !!(option && option.legendary && !this.spawnCanAddLegendary)
    },
    isAddSpawnFilterActive (key) {
      if (this.isAddSpawnFilterDisabled(key)) return false
      return !!(this.addSpawnModal.activeFilters || {})[key]
    },
    isAddSpawnFilterDisabled (key) {
      return this.censorRarityTiers && key === 'rarities'
    },
    setAddSpawnFilterActive (key, active) {
      if (this.isAddSpawnFilterDisabled(key)) return
      this.$set(this.addSpawnModal.activeFilters, key, !!active)
      if (key === 'hideAdded') {
        this.savePreferences()
      }
    },
    isAddSpawnFilterCollapsed (key) {
      return !!(this.addSpawnModal.collapsedFilters || {})[key]
    },
    toggleAddSpawnFilterCollapsed (key) {
      if (this.isAddSpawnFilterDisabled(key)) return
      this.$set(this.addSpawnModal.collapsedFilters, key, !this.isAddSpawnFilterCollapsed(key))
      this.savePreferences()
    },
    addSpawnFilterSelectedCount (key) {
      if (key === 'topUsage' || key === 'hideAdded') return this.isAddSpawnFilterActive(key) ? 'Active' : 'Off'
      if (this.isAddSpawnFilterDisabled(key)) return 'Off'
      const values = this.addSpawnModal.filters[key] || []
      if (!values.length) return 'All'
      return values.length + ' selected'
    },
    addSpawnFilterOptionText (filter, option) {
      if (this.censorRarityTiers && filter && filter.key === 'rarities') return this.censoredTierLabel()
      return option.text
    },
    isAddSpawnFilterValueSelected (key, value) {
      return (this.addSpawnModal.filters[key] || []).indexOf(value) !== -1
    },
    toggleAddSpawnFilterValue (key, value, selected) {
      if (this.isAddSpawnFilterDisabled(key)) return
      const values = (this.addSpawnModal.filters[key] || []).slice()
      const index = values.indexOf(value)
      if (selected && index === -1) values.push(value)
      if (!selected && index !== -1) values.splice(index, 1)
      this.$set(this.addSpawnModal.filters, key, values)
    },
    resetAddSpawnFilters () {
      const hideAdded = this.isAddSpawnFilterActive('hideAdded')
      this.addSpawnModal.activeFilters = {
        types: true,
        rarities: !this.censorRarityTiers,
        habitats: true,
        evolutionStages: true,
        regions: true,
        events: true,
        natures: true,
        topUsage: false,
        hideAdded
      }
      this.addSpawnModal.filters = {
        types: [],
        rarities: [],
        habitats: [],
        evolutionStages: [],
        regions: [],
        events: [],
        natures: []
      }
      this.addSpawnModal.search = ''
    },
    defaultAddSpawnTimeKeys () {
      return TIME_PERIODS.map(period => period.key)
    },
    normalizeTimeKeys (keys) {
      const selected = (keys || []).reduce((result, key) => {
        result[String(key)] = true
        return result
      }, Object.create(null))
      return TIME_PERIODS.map(period => period.key).filter(key => selected[key])
    },
    timeKeysSignature (keys) {
      return this.normalizeTimeKeys(keys).join('|')
    },
    addSpawnSelectedTimeKeys (selectionKey) {
      const entry = (this.addSpawnModal.selected || {})[String(selectionKey)]
      if (!entry || !Array.isArray(entry.timeKeys)) return this.defaultAddSpawnTimeKeys()
      return this.normalizeTimeKeys(entry.timeKeys)
    },
    isAddSpawnSelectedTimeActive (pokemon, key) {
      return this.addSpawnSelectedTimeKeys(pokemon && pokemon.selectionKey).indexOf(key) !== -1
    },
    toggleAddSpawnSelectedTime (selectionKey, key) {
      const selected = this.addSpawnModal.selected || {}
      const entry = selected[String(selectionKey)]
      if (!entry) return
      const keys = this.addSpawnSelectedTimeKeys(selectionKey)
      const index = keys.indexOf(key)
      if (index === -1) {
        keys.push(key)
      } else {
        keys.splice(index, 1)
      }
      this.$set(this.addSpawnModal.selected, String(selectionKey), Object.assign({}, entry, {
        timeKeys: this.normalizeTimeKeys(keys)
      }))
    },
    addSpawnExistingTimeKeys (spawn) {
      const key = this.spawnKey(spawn)
      const overrides = this.addSpawnModal.existingTimeOverrides || {}
      if (this.cacheHas(overrides, key)) return this.normalizeTimeKeys(overrides[key])
      return this.activeTimeKeys(spawn)
    },
    isAddSpawnExistingTimeActive (spawn, key) {
      return this.addSpawnExistingTimeKeys(spawn).indexOf(key) !== -1
    },
    toggleAddSpawnExistingTime (spawn, key) {
      if (this.isAddSpawnExistingRemoved(spawn)) return
      const spawnKey = this.spawnKey(spawn)
      const keys = this.addSpawnExistingTimeKeys(spawn).slice()
      const index = keys.indexOf(key)
      if (index === -1) {
        keys.push(key)
      } else {
        keys.splice(index, 1)
      }
      const normalized = this.normalizeTimeKeys(keys)
      if (this.timeKeysSignature(normalized) === this.timeKeysSignature(this.activeTimeKeys(spawn))) {
        this.$delete(this.addSpawnModal.existingTimeOverrides, spawnKey)
        return
      }
      this.$set(this.addSpawnModal.existingTimeOverrides, spawnKey, normalized)
    },
    applyTimeKeysToSpawn (spawn, keys) {
      const normalized = this.normalizeTimeKeys(keys)
      if (!normalized.length) {
        this.$set(spawn, 'noTimeSelected', true)
        this.$set(spawn, 'start', 0)
        this.$set(spawn, 'end', 0)
        return spawn
      }
      const range = this.timeRangeForKeys(normalized)
      this.$set(spawn, 'noTimeSelected', false)
      this.$set(spawn, 'start', range.start)
      this.$set(spawn, 'end', range.end)
      return spawn
    },
    assignTimeKeysToSpawn (spawn, keys) {
      const normalized = this.normalizeTimeKeys(keys)
      if (!normalized.length) {
        spawn.noTimeSelected = true
        spawn.start = 0
        spawn.end = 0
        return spawn
      }
      const range = this.timeRangeForKeys(normalized)
      spawn.noTimeSelected = false
      spawn.start = range.start
      spawn.end = range.end
      return spawn
    },
    applyAddSpawnExistingTimeOverride (spawn) {
      const key = this.spawnKey(spawn)
      const overrides = this.addSpawnModal.existingTimeOverrides || {}
      if (!this.cacheHas(overrides, key)) return spawn
      return this.assignTimeKeysToSpawn(spawn, overrides[key])
    },
    addSpawnExistingEffectiveSpawn (spawn) {
      return this.applyAddSpawnExistingTimeOverride(this.cloneSpawn(spawn))
    },
    clearRepelCache () {
      this.repelMapCache = Object.create(null)
    },
    clearMoveAnalysisCache () {
      this.moveCache = Object.create(null)
      this.safeLevelRangeCache = Object.create(null)
      this.selfHarmingUnsafeLevelCache = Object.create(null)
    },
    markIconError (key) {
      this.$set(this.iconErrors, key, true)
    },
    warningGroupExpanded (key) {
      return !!this.expandedWarningGroups[key]
    },
    toggleWarningGroup (key) {
      this.$set(this.expandedWarningGroups, key, !this.warningGroupExpanded(key))
    },
    hiddenWarningCount (warnings, key, limit) {
      if (this.warningGroupExpanded(key)) return 0
      return Math.max((warnings || []).length - limit, 0)
    },
    warningRows (warnings, key, limit) {
      if (this.warningGroupExpanded(key)) return warnings || []
      return (warnings || []).slice(0, limit)
    },
    warningHtml (warning) {
      if (warning && warning.html) return warning.html
      return this.escapeHtml(warning && warning.text ? warning.text : '')
    },
    formatPeriodLabels (periods) {
      return (periods || []).map(period => period.label).join('/')
    },
    formatSpawnLevelRange (spawn) {
      const min = Number(spawn && spawn.minLVL ? spawn.minLVL : 0)
      const max = Number(spawn && spawn.maxLVL ? spawn.maxLVL : min)
      return min === max ? 'Lv. ' + min : 'Lv. ' + min + '-' + max
    },
    timeLabelForSpawn (spawn) {
      const labels = this.activeTimeKeys(spawn).map(key => {
        const period = TIME_PERIODS.find(entry => entry.key === key)
        return period ? period.label : key
      })
      return labels.length ? labels.join('/') : 'Time None'
    },
    selectMap (mapName) {
      if (mapName !== this.activeMap && !this.mapSwitchLoading) {
        this.beginMapSwitchLoading()
      }
      this.activeMap = mapName
    },
    selectMapEntry (map) {
      if (!map) return
      const nextMap = map.name || ''
      const nextBatchID = map.batchID || null
      const mapChanged = nextMap !== this.activeMap
      const batchChanged = Number(nextBatchID || 0) !== Number(this.activeBatchId || 0)
      if ((mapChanged || batchChanged) && !this.mapSwitchLoading) {
        this.beginMapSwitchLoading()
      }
      this.activeBatchId = nextBatchID
      if (mapChanged) {
        this.activeMap = nextMap
      } else if (batchChanged) {
        this.refreshMapImageState()
      }
    },
    resetMapSelection () {
      this.activeMap = ''
      this.activeNatureHighlight = ''
      this.activeNatureHighlightArea = ''
      this.clearMapSearchValue()
      this.mapSearchMode = 'map'
      this.selectedSearchEntity = null
      if (this.maps.length) {
        this.activeMap = this.maps[0].name
      }
    },
    isNatureHighlightActive (nature, area) {
      return this.activeNatureHighlight === nature && this.activeNatureHighlightArea === area
    },
    toggleNatureHighlight (nature, area) {
      if (this.isNatureHighlightActive(nature, area)) {
        this.activeNatureHighlight = ''
        this.activeNatureHighlightArea = ''
        return
      }
      this.activeNatureHighlight = nature
      this.activeNatureHighlightArea = area
    },
    natureStatsForSpawns (spawns) {
      const rows = (spawns || []).filter(spawn => this.naturesForSpawn(spawn).length)
      const denominator = rows.length
      if (denominator < 3) return []
      const counts = Object.create(null)
      rows.forEach(spawn => {
        this.naturesForSpawn(spawn).forEach(nature => {
          counts[nature] = (counts[nature] || 0) + 1
        })
      })
      return Object.keys(counts)
        .filter(nature => counts[nature] >= 3)
        .map(nature => ({
          name: nature,
          count: counts[nature],
          percent: counts[nature] / denominator * 100
        }))
        .sort((a, b) => {
          if (b.percent !== a.percent) return b.percent - a.percent
          return a.name.localeCompare(b.name)
        })
    },
    formatNaturePercent (value) {
      const numeric = Number(value || 0)
      if (!Number.isFinite(numeric)) return '0%'
      return numeric.toFixed(1) + '%'
    },
    spawnCardClasses (spawn, baseClass) {
      return {
        [baseClass]: true,
        'spawn-card-warning': this.spawnHasWarning(spawn),
        'spawn-card-nature-highlight': this.isSpawnNatureHighlighted(spawn)
      }
    },
    stackedSpawnCardClasses (spawn, baseClass, group) {
      return {
        [baseClass]: true,
        'spawn-card-warning': this.stackedSpawnHasWarning(spawn, group)
      }
    },
    isSpawnNatureHighlighted (spawn) {
      if (!this.activeNatureHighlight || !this.activeNatureHighlightArea) return false
      if (!this.spawnSupportsNature(spawn, this.activeNatureHighlight)) return false
      if (this.activeNatureHighlightArea === 'land') return !this.isWaterSpawn(spawn)
      if (this.activeNatureHighlightArea === 'water') return this.isWaterSpawn(spawn) && !this.isFishingOnly(spawn)
      return false
    },
    spawnHasWarning (spawn) {
      const key = this.spawnKey(spawn)
      return !!(this.selfHarmingWarningBySpawnKey[key] || this.repelInfoBySpawnKey[key] || this.natureConflictBySpawnKey[key])
    },
    stackedSpawnHasWarning (spawn, group) {
      if (!this.showWarnings) return false
      return this.spawnHasSelfHarmingWarning(spawn) || !!this.stackedRepelInfoForSpawn(spawn, group)
    },
    selfHarmingMovesForSpawn (spawn) {
      return this.possibleMoves(spawn).filter(move => this.isSelfHarmingMove(move, spawn))
    },
    spawnHasSelfHarmingWarning (spawn) {
      return this.selfHarmingMovesForSpawn(spawn).length > 0
    },
    selfHarmingTooltip (spawn) {
      const moves = this.selfHarmingMovesForSpawn(spawn)
      if (!moves.length) return ''
      const lines = moves.map(move => {
        const levelRange = move.rangeText || (spawn.minLVL + '-' + spawn.maxLVL)
        return this.escapeHtml(this.pokemonName(spawn) + ' can know ' + move.name + ' at Lv. ' + levelRange + '.')
      })
      const allLevelRows = this.allSelfHarmingMoveLevelRows(spawn)
      if (allLevelRows.length) {
        lines.push('<span style="color:#9fb0bf;">All self-harming learn levels:</span>')
        allLevelRows.forEach(row => {
          lines.push(this.escapeHtml(row.name + ': Lv. ' + row.levels))
        })
      }
      return '<strong>Self-harming move warning</strong><br>' + lines.join('<br>')
    },
    allSelfHarmingMoveLevelRows (spawn) {
      const fullRangeSpawn = Object.assign({}, spawn, { minLVL: 1, maxLVL: 100 })
      const seen = Object.create(null)
      return this.possibleMoves(fullRangeSpawn)
        .filter(move => this.isSelfHarmingMove(move, spawn))
        .map(move => {
          const key = String(Number(move.skillID || 0) || this.normalizeMoveName(move.name))
          if (seen[key]) return null
          seen[key] = true
          return {
            name: move.name,
            levels: move.rangeText || this.formatLevelRanges(this.compactLevelRanges(move.levels || []))
          }
        })
        .filter(row => row && row.levels)
    },
    stackedRepelInfoForSpawn (spawn, group) {
      if (!this.showWarnings || !group) return null
      return (group.repelInfoByKey || Object.create(null))[this.spawnKey(spawn)] || null
    },
    stackedRepelTooltip (spawn, group) {
      return this.repelInfoTooltip(this.stackedRepelInfoForSpawn(spawn, group))
    },
    stackedProbabilityLabel (spawn, rows, key) {
      const value = this.spawnProbabilityForRows(spawn, key, rows)
      return value === null ? '-' : this.formatPercent(value)
    },
    pvpUsageRanksForSpawn (spawn) {
      return this.usageRanksByDexID[Number(spawn.dexID || 0)] || []
    },
    pvpUsageTooltip (rank) {
      const name = rank && rank.name ? rank.name : 'This Pokemon'
      return name + ' is ' + this.ordinal(rank.rank) + ' Place in Normal Ranked PvP Usage Stats from the last 30 days.'
    },
    ordinal (value) {
      const number = Number(value || 0)
      if (!number) return '-'
      const mod100 = number % 100
      if (mod100 >= 11 && mod100 <= 13) return number + 'th'
      const mod10 = number % 10
      if (mod10 === 1) return number + 'st'
      if (mod10 === 2) return number + 'nd'
      if (mod10 === 3) return number + 'rd'
      return number + 'th'
    },
    selectSidebarItem (item) {
      if (this.editMode) return
      if (item.type === 'pokemonOption') {
        this.selectedSearchEntity = {
          type: 'pokemon',
          dexID: item.dexID,
          name: item.name
        }
        this.clearMapSearchValue()
        return
      }
      if (item.type === 'regionOption') {
        this.selectedSearchEntity = {
          type: 'region',
          name: item.name
        }
        this.clearMapSearchValue()
        return
      }
      if (item.map) {
        this.selectMapEntry(item.map)
      }
    },
    clearSearchEntity () {
      this.selectedSearchEntity = null
      this.clearMapSearchValue()
    },
    sidebarItemKey (item) {
      if (item.type === 'pokemonOption') return 'pokemon-' + item.dexID
      if (item.type === 'regionOption') return 'region-' + item.name
      if (item.type === 'pokemonMap') return 'pokemon-map-' + item.dexID + '-' + item.map.name
      if (item.type === 'regionMap') return 'region-map-' + item.region + '-' + item.map.name
      return 'map-' + (item.map.batchID || item.map.name)
    },
    exactPokemonSearchMatch () {
      const search = this.mapSearchText
      if (!search) return null
      return this.pokemonOptionsByName[search] || null
    },
    exactRegionSearchMatch () {
      const search = this.mapSearchText
      if (!search) return null
      return this.regionOptionsByName[search] || null
    },
    pokemonMapItemsForDexID (dexID) {
      const numericDexID = Number(dexID || 0)
      if (!numericDexID) return []
      const cacheKey = String(numericDexID)
      if (this.cacheHas(this.pokemonMapItemsCache, cacheKey)) return this.pokemonMapItemsCache[cacheKey]

      const grouped = Object.create(null)
      const mapNamesByKey = Object.create(null)
      ;(this.spawnsByDexID[numericDexID] || []).forEach(spawn => {
        const mapName = spawn.map || 'Unknown'
        const key = mapListKey(mapName)
        if (!key) return
        if (!grouped[key]) {
          grouped[key] = []
          mapNamesByKey[key] = mapName
        }
        grouped[key].push(spawn)
      })

      const mapKeys = Object.keys(grouped).sort((a, b) => SPAWN_NAME_COLLATOR.compare(mapNamesByKey[a], mapNamesByKey[b]))
      const firstRow = mapKeys.length && grouped[mapKeys[0]].length ? grouped[mapKeys[0]][0] : null
      const pokemonName = firstRow ? this.pokemonName(firstRow) : 'Unknown Pokemon'
      const items = mapKeys.map(key => {
        const rows = grouped[key]
        const mapName = mapNamesByKey[key]
        const map = this.mapByLowerName[key] || { name: mapName, searchName: searchText(mapName), land: 0, water: 0 }
        const representative = this.representativeTierSpawn(rows)
        return {
          type: 'pokemonMap',
          map,
          dexID: numericDexID,
          pokemonName,
          memberOnly: rows.length > 0 && rows.every(spawn => spawn.memberOnly),
          repelInfo: null,
          tierLabel: this.censorRarityTiers ? this.censoredTierLabel() : this.searchTierLabel(representative, numericDexID),
          tierGroup: representative ? this.rarityGroupKey(representative) : this.tierGroupFromTier(this.referenceTierForPokemon(numericDexID))
        }
      })
      this.pokemonMapItemsCache[cacheKey] = items
      return items
    },
    pokemonMapItems (entity) {
      const rawSearch = this.mapSearchText
      const search = rawSearch === searchText(entity.name).trim() ? '' : rawSearch
      return this.pokemonMapItemsForDexID(entity.dexID)
        .filter(item => !search || item.map.searchName.indexOf(search) !== -1)
        .map(item => this.showWarnings && this.mapAnalysisReady
          ? Object.assign({}, item, { repelInfo: this.repelInfoForMapPokemon(item.map.name, item.dexID) })
          : item)
    },
    regionMapItems (entity) {
      const rawSearch = this.mapSearchText
      const search = rawSearch === searchText(entity.name).trim() ? '' : rawSearch
      const region = this.regionOptionsByName[searchText(entity.name)] || { mapItems: [] }
      return region.mapItems
        .filter(map => !search || map.searchName.indexOf(search) !== -1)
        .map(map => ({ type: 'regionMap', region: entity.name, map }))
    },
    representativeTierSpawn (spawns) {
      if (!spawns || !spawns.length) return null
      return spawns.reduce((best, spawn) => !best || this.raritySortValue(spawn) > this.raritySortValue(best) ? spawn : best, null)
    },
    referenceTierForPokemon (dexID) {
      const tiers = this.referenceData && this.referenceData.pokemonRarityTiers ? this.referenceData.pokemonRarityTiers : {}
      return tiers[String(Number(dexID || 0))] || ''
    },
    referenceTierForSpawn (spawn) {
      return this.referenceTierForPokemon(spawn.dexID)
    },
    predefinedTierSortValueForSpawn (spawn) {
      const tier = this.referenceTierForSpawn(spawn)
      if (!tier) return 100
      return this.raritySortValueForTier(tier)
    },
    referenceTierSortValuesForPokemon (dexID) {
      const tier = this.referenceTierForPokemon(dexID)
      const tiers = Array.isArray(tier) ? tier : [tier]
      return tiers.map(entry => this.raritySortValueForTier(entry)).filter(value => value !== 99)
    },
    isSharedNatureWarningTier (spawn) {
      const value = this.raritySortValue(spawn)
      return value >= 5 && value <= 9.5
    },
    repelTierSortValue (spawn) {
      const spawnTier = this.raritySortValue(spawn)
      if (spawnTier !== 99) return spawnTier
      const referenceTier = this.predefinedTierSortValueForSpawn(spawn)
      return referenceTier === 100 ? 99 : referenceTier
    },
    normalizePokemonLookupName (name) {
      return String(name || '').trim().toLowerCase()
    },
    naturesForSpawn (spawn) {
      return this.preferredNaturesByDexID[String(Number(spawn.dexID || 0))] || []
    },
    spawnSupportsNature (spawn, nature) {
      return this.naturesForSpawn(spawn).indexOf(nature) !== -1
    },
    buildNatureConflictWarnings (spawns, area) {
      const sourceSpawns = (spawns || []).filter(spawn => this.isSharedNatureWarningTier(spawn))
      const grouped = Object.create(null)
      TIME_PERIODS.forEach(period => {
        const periodSpawns = sourceSpawns.filter(spawn => this.isTimeActive(spawn, period.key))
        const byNature = Object.create(null)
        periodSpawns.forEach(spawn => {
          const dexID = Number(spawn.dexID || 0)
          if (!dexID) return
          this.naturesForSpawn(spawn).forEach(nature => {
            if (!byNature[nature]) byNature[nature] = Object.create(null)
            if (!byNature[nature][dexID]) byNature[nature][dexID] = spawn
          })
        })
        Object.keys(byNature).forEach(nature => {
          const natureSpawns = Object.values(byNature[nature])
          if (natureSpawns.length < 3) return
          const dexIDs = natureSpawns.map(spawn => Number(spawn.dexID || 0)).sort((a, b) => a - b)
          const key = area + '-' + nature + '-' + dexIDs.join('-')
          if (!grouped[key]) {
            grouped[key] = {
              key,
              nature,
              dexIDs,
              spawns: natureSpawns,
              periods: []
            }
          }
          grouped[key].periods.push(period)
        })
      })

      return Object.values(grouped)
        .map(entry => {
          const dexIDSet = entry.dexIDs.reduce((result, dexID) => {
            result[dexID] = true
            return result
          }, Object.create(null))
          const spawnKeys = sourceSpawns
            .filter(spawn => dexIDSet[Number(spawn.dexID || 0)] && this.spawnSupportsNature(spawn, entry.nature))
            .map(spawn => this.spawnKey(spawn))

          const tierLabel = this.sharedNatureTierRangeLabel(entry.spawns)
          const pokemonNames = entry.spawns.map(spawn => this.pokemonName(spawn)).sort().join(', ')
          const periods = this.formatPeriodLabels(entry.periods)
          const text = entry.nature + ' is shared by ' + tierLabel + ' ' + area + ' spawns ' + pokemonNames + ' during ' + periods + '.'
          const html = this.escapeHtml(entry.nature + ' is shared by ') +
            (this.censorRarityTiers ? '<span class="spawn-censored-value">' + this.escapeHtml(this.censoredTierLabel()) + '</span>' : this.escapeHtml(tierLabel)) +
            this.escapeHtml(' ' + area + ' spawns ' + pokemonNames + ' during ' + periods + '.')
          return {
            key: 'nature-' + entry.key,
            nature: entry.nature,
            dexIDs: entry.dexIDs,
            spawnKeys,
            text,
            html
          }
        })
        .sort((a, b) => a.text.localeCompare(b.text, undefined, { numeric: true }))
    },
    sharedNatureTierRangeLabel (spawns) {
      const tiers = (spawns || [])
        .map(spawn => this.raritySortValue(spawn))
        .filter(value => value >= 5 && value <= 9.5)
        .sort((a, b) => a - b)
        .filter((value, index, list) => index === 0 || value !== list[index - 1])
      if (!tiers.length) return 'T5-9.5'
      const first = tiers[0]
      const last = tiers[tiers.length - 1]
      if (first === last) return this.compactTierLabel(first)
      return this.compactTierLabel(first) + '-' + this.compactTierLabel(last).replace(/^T/, '')
    },
    compactTierLabel (tier) {
      return 'T' + String(tier).replace(/\.0$/, '')
    },
    uniqueDexIDs (spawns) {
      const seen = Object.create(null)
      return (spawns || [])
        .map(spawn => Number(spawn.dexID || 0))
        .filter(dexID => dexID)
        .filter(dexID => {
          if (seen[dexID]) return false
          seen[dexID] = true
          return true
        })
    },
    repelInfoForMap (mapName) {
      if (!this.showWarnings || !mapName) return Object.create(null)
      const cacheKey = [
        this.workflowView,
        mapName,
        this.membershipActive ? 'member' : 'free',
        this.spawnsUpdatedAt || '',
        this.workflowUpdatedAt || '',
        this.referenceDataUpdatedAt || ''
      ].join('|')
      if (!this.repelMapCache[cacheKey]) {
        this.repelMapCache[cacheKey] = this.buildRepelInfoBySpawnKey(this.sidebarSpawnsForMapListName(mapName))
      }
      return this.repelMapCache[cacheKey]
    },
    repelInfoForMapPokemon (mapName, dexID) {
      if (!this.showWarnings) return null
      const infoByKey = this.repelInfoForMap(mapName)
      const rows = this.sidebarSpawnsForMapListName(mapName).filter(spawn => Number(spawn.dexID || 0) === Number(dexID || 0))
      for (let i = 0; i < rows.length; i++) {
        const info = infoByKey[this.spawnKey(rows[i])]
        if (info) return info
      }
      return null
    },
    repelInfoForPokemonRows (rows) {
      if (!this.showWarnings) return null
      for (let i = 0; i < (rows || []).length; i++) {
        const spawn = rows[i]
        const info = this.repelInfoForMap(spawn.map || 'Unknown')[this.spawnKey(spawn)]
        if (info) return info
      }
      return null
    },
    buildRepelInfoBySpawnKey (mapSpawns) {
      const bestBySpawn = Object.create(null)
      const analysisSpawns = (mapSpawns || []).filter(spawn => this.membershipActive || !spawn.memberOnly)
      TIME_PERIODS.forEach(period => {
        const periodSpawns = analysisSpawns.filter(spawn => this.isTimeActive(spawn, period.key))
        const activeDexCount = this.uniqueDexIDs(periodSpawns).length
        if (activeDexCount < 2) return
        const maxLevel = periodSpawns.reduce((max, spawn) => Math.max(max, Number(spawn.maxLVL || 0)), 0)
        for (let level = 1; level <= maxLevel; level++) {
          const eligible = periodSpawns.filter(spawn => Number(spawn.maxLVL || 0) >= level)
          const eligibleDexIDs = this.uniqueDexIDs(eligible)
          if (!eligible.length || eligibleDexIDs.length >= activeDexCount) continue

          let targets = []
          let reason = ''
          if (eligibleDexIDs.length === 1) {
            targets = eligible
            reason = 'solo'
          } else {
            const highTier = eligible.filter(spawn => this.repelTierSortValue(spawn) >= 7)
            if (highTier.length && highTier.length === eligible.length) {
              targets = highTier
              reason = 'high-tier'
            }
          }

          targets.forEach(spawn => {
            const dexID = Number(spawn.dexID || 0)
            if (!dexID) return
            const key = this.spawnKey(spawn)
            if (!bestBySpawn[key] || level < bestBySpawn[key].minLevel || (level === bestBySpawn[key].minLevel && reason === 'solo' && bestBySpawn[key].reason !== 'solo')) {
              bestBySpawn[key] = {
                dexID,
                name: this.pokemonName(spawn),
                minLevel: level,
                reason,
                periods: [period]
              }
            } else if (level === bestBySpawn[key].minLevel && reason === bestBySpawn[key].reason && !bestBySpawn[key].periods.some(existing => existing.key === period.key)) {
              bestBySpawn[key].periods.push(period)
            }
          })
        }
      })
      return bestBySpawn
    },
    repelInfoForSpawn (spawn) {
      return this.repelInfoBySpawnKey[this.spawnKey(spawn)] || null
    },
    repelTooltip (spawn) {
      const info = this.repelInfoForSpawn(spawn)
      if (!info) return ''
      return this.repelInfoTooltip(info)
    },
    repelInfoTooltip (info) {
      if (!info) return ''
      const reason = info.reason === 'solo' ? 'can be isolated' : 'leaves only T7+ Pokemon'
      return this.escapeHtml(info.name + ' ' + reason + ' with Repel from Lv. ' + info.minLevel + ' during ' + this.formatPeriodLabels(info.periods) + '.')
    },
    searchTierLabel (spawn, dexID) {
      const tier = spawn ? this.rarityTier(spawn) : this.referenceTierForPokemon(dexID)
      return this.tierDisplayLabel(tier)
    },
    censoredTierLabel () {
      return 'Tier 5'
    },
    censoredRarityLabel () {
      return '0.0005'
    },
    censoredProbabilityLabel () {
      return '00.0%'
    },
    shouldCensorProbabilityLabel (label) {
      return this.censorRarityTiers && String(label || '').trim() !== '-'
    },
    tierDisplayLabel (tier) {
      if (Array.isArray(tier)) {
        if (!tier.length) return 'Custom'
        return tier.map(entry => this.tierDisplayLabel(entry)).join(', ')
      }
      const tierText = String(tier || '').trim()
      if (!tierText) return 'Custom'
      if (this.isTierTen(tierText)) return 'Tier 10'
      const match = /^T(\d+(?:\.\d+)?)$/.exec(tierText)
      return match ? 'Tier ' + match[1] : 'Custom'
    },
    rarityTierSelectOptions (spawn) {
      const options = this.rarityTierOptions.slice()
      const current = this.rarityTier(spawn)
      if (!options.some(option => option.value === current)) {
        options.unshift({ value: current, text: current, disabled: true })
      }
      return options
    },
    addSpawnRaritySortValue (pokemon) {
      const values = this.referenceTierSortValuesForPokemon(pokemon.dexID)
      if (!values.length) return 99
      return Math.max.apply(null, values)
    },
    addSpawnPokemonTierLabel (dexID) {
      return this.tierDisplayLabel(this.referenceTiersForPokemon(dexID))
    },
    addSpawnPokemonTierDisplayLabel (dexID) {
      return this.censorRarityTiers ? this.censoredTierLabel() : this.addSpawnPokemonTierLabel(dexID)
    },
    tierGroupFromTier (tier) {
      const sortValue = this.raritySortValueForTier(tier)
      if (sortValue >= 1 && sortValue <= 3) return 'common'
      if (sortValue >= 4 && sortValue <= 6) return 'uncommon'
      if (sortValue >= 7 && sortValue <= 9.5) return 'rare'
      if (sortValue === 10) return 'unobtainable'
      return this.tierClassKey(tier)
    },
    loadPreferences () {
      try {
        const raw = window.localStorage.getItem(SPAWN_EDITOR_PREFS_KEY)
        if (!raw) return
        const prefs = JSON.parse(raw)
        if (SORT_OPTIONS.some(option => option.value === prefs.sortKey)) {
          this.sortKey = prefs.sortKey
        }
        if (prefs.sortDirection === 'asc' || prefs.sortDirection === 'desc') {
          this.sortDirection = prefs.sortDirection
        }
        if (typeof prefs.membershipActive === 'boolean') {
          this.membershipActive = prefs.membershipActive
        }
        if (typeof prefs.showWarnings === 'boolean') {
          this.showWarnings = prefs.showWarnings
        }
        if (typeof prefs.comparisonExpanded === 'boolean') {
          this.comparisonExpanded = prefs.comparisonExpanded
        }
        if (typeof prefs.addSpawnHideAdded === 'boolean') {
          this.$set(this.addSpawnModal.activeFilters, 'hideAdded', prefs.addSpawnHideAdded)
        }
        if (prefs.addSpawnCollapsedFilters && typeof prefs.addSpawnCollapsedFilters === 'object') {
          ADD_SPAWN_FILTER_KEYS.forEach(key => {
            if (typeof prefs.addSpawnCollapsedFilters[key] === 'boolean') {
              this.$set(this.addSpawnModal.collapsedFilters, key, prefs.addSpawnCollapsedFilters[key])
            }
          })
        }
      } catch (e) {
        console.warn('Failed to load spawn editor preferences.', e)
      }
    },
    savePreferences () {
      try {
        window.localStorage.setItem(SPAWN_EDITOR_PREFS_KEY, JSON.stringify({
          sortKey: this.sortKey,
          sortDirection: this.sortDirection,
          membershipActive: this.membershipActive,
          showWarnings: this.showWarnings,
          comparisonExpanded: this.comparisonExpanded,
          addSpawnHideAdded: this.isAddSpawnFilterActive('hideAdded'),
          addSpawnCollapsedFilters: this.addSpawnModal.collapsedFilters
        }))
      } catch (e) {
        console.warn('Failed to save spawn editor preferences.', e)
      }
    },
    toggleComparisonExpanded () {
      this.comparisonExpanded = !this.comparisonExpanded
      this.savePreferences()
    },
    toggleSortDirection () {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
    },
    passesActiveFilters (spawn) {
      if (!this.membershipActive && spawn.memberOnly) return false
      if (this.activeTimeFilter !== 'all' && !this.isTimeActive(spawn, this.activeTimeFilter)) return false
      return true
    },
    compareNames (a, b) {
      return SPAWN_NAME_COLLATOR.compare(this.pokemonName(a), this.pokemonName(b))
    },
    raritySortValue (spawn) {
      return this.raritySortValueForTier(this.rarityTier(spawn))
    },
    raritySortValueForTier (tier) {
      if (Array.isArray(tier)) {
        const values = tier.map(entry => this.raritySortValueForTier(entry))
        if (!values.length) return 99
        return Math.max.apply(null, values)
      }
      const tierText = String(tier || '').trim()
      const match = /^T(\d+(?:\.\d+)?)$/.exec(tierText)
      if (match) return Number(match[1])
      if (this.isTierTen(tierText)) return 10
      return 99
    },
    compareSpawns (a, b) {
      const direction = this.sortDirection === 'desc' ? -1 : 1
      let primaryCompare = 0
      if (this.sortKey === 'dexID') {
        primaryCompare = Number(a.dexID || 0) - Number(b.dexID || 0)
      } else if (this.sortKey === 'rarity') {
        primaryCompare = this.raritySortValue(a) - this.raritySortValue(b)
        if (primaryCompare === 0) {
          primaryCompare = Number(b.rarity || 0) - Number(a.rarity || 0)
        }
      } else {
        primaryCompare = this.compareNames(a, b)
      }
      if (primaryCompare !== 0) return direction * primaryCompare

      const nameCompare = this.compareNames(a, b)
      if (nameCompare !== 0) return direction * nameCompare
      const dexCompare = Number(a.dexID || 0) - Number(b.dexID || 0)
      if (dexCompare !== 0) return direction * dexCompare
      const levelCompare = Number(a.minLVL || 0) - Number(b.minLVL || 0)
      if (levelCompare !== 0) return direction * levelCompare
      const idCompare = Number(a.id || 0) - Number(b.id || 0)
      return direction * idCompare
    },
    spawnKey (spawn) {
      return String(spawn.area || 'land') + '-' + String(spawn.id || '') + '-' + String(spawn.dexID || '')
    },
    isWaterSpawn (spawn) {
      const area = String(spawn.area || '').toLowerCase()
      return area === 'surf' || area === 'water'
    },
    isFishable (spawn) {
      return Number(spawn.required_rod || 0) > 0
    },
    isFishingOnly (spawn) {
      return this.isWaterSpawn(spawn) &&
        Number(spawn.x1 || 0) === 0 &&
        Number(spawn.x2 || 0) === 0 &&
        Number(spawn.y1 || 0) === 0 &&
        Number(spawn.y2 || 0) === 0
    },
    hasSurfEncounter (spawn) {
      return this.isWaterSpawn(spawn) && !this.isFishingOnly(spawn)
    },
    rodLabel (spawn) {
      const rod = Number(spawn.required_rod || 0)
      if (rod === 1) return 'Old Rod+'
      if (rod === 2) return 'Good Rod+'
      if (rod === 3) return 'Super Rod'
      return 'Not fishable'
    },
    rodIconForSpawn (spawn) {
      const rod = Number(spawn.required_rod || 0)
      const match = FISHING_RODS.find(candidate => candidate.requiredRod === rod)
      return match ? match.icon : ''
    },
    rodIconForKey (key) {
      const match = FISHING_RODS.find(candidate => candidate.key === key)
      return match ? match.icon : ''
    },
    pokemonName (spawn) {
      if (spawn.pokemon) return spawn.pokemon
      const monster = MONSTER_BY_ID[Number(spawn.dexID)]
      return monster && monster.Name ? monster.Name : 'Unknown Pokemon'
    },
    pokemonSpriteUrl (id) {
      const numeric = Number(id)
      if (!numeric || Number.isNaN(numeric)) return ''
      return POKEAPI_POKEMON_SPRITE_BASE + numeric + '.png'
    },
    handleSpriteError (spawn) {
      this.$set(this.spriteErrors, this.spawnKey(spawn), true)
    },
    handleStackSpriteError (group, spawn) {
      const batchID = group && group.batch ? group.batch.id : 'unknown'
      this.$set(this.spriteErrors, 'stack-' + batchID + '-' + this.spawnKey(spawn), true)
    },
    handleSearchSpriteError (item) {
      this.$set(this.spriteErrors, 'search-' + item.dexID, true)
    },
    handleSearchMapSpriteError (item) {
      this.$set(this.spriteErrors, 'search-map-' + item.dexID + '-' + item.map.name, true)
    },
    handleAddSpawnSpriteError (pokemon) {
      this.$set(this.spriteErrors, 'add-' + pokemon.dexID, true)
    },
    handleAddSpawnSelectedSpriteError (pokemon) {
      this.$set(this.spriteErrors, 'add-selected-' + pokemon.dexID, true)
    },
    handleAddSpawnExistingSpriteError (spawn) {
      this.$set(this.spriteErrors, 'add-existing-' + this.spawnKey(spawn), true)
    },
    cacheHas (cache, key) {
      return Object.prototype.hasOwnProperty.call(cache || {}, key)
    },
    possibleMoves (spawn) {
      const dexID = Number(spawn.dexID || 0)
      const minLevel = Math.max(Number(spawn.minLVL || 0), 1)
      const maxLevel = Math.max(Number(spawn.maxLVL || minLevel), minLevel)
      const cacheKey = dexID + '-' + minLevel + '-' + maxLevel
      if (this.cacheHas(this.moveCache, cacheKey)) return this.moveCache[cacheKey]

      const learnset = this.movesByMonsterID[dexID] || []
      if (!learnset.length) {
        this.moveCache[cacheKey] = []
        return []
      }

      const selected = Object.create(null)
      const available = []
      let nextMoveIndex = 0
      for (let level = 1; level <= maxLevel; level++) {
        while (nextMoveIndex < learnset.length && learnset[nextMoveIndex].level <= level) {
          available.push(learnset[nextMoveIndex])
          nextMoveIndex++
        }
        if (level < minLevel) continue
        const lastFour = available.slice(Math.max(available.length - 4, 0))
        lastFour.forEach(move => {
          const key = String(Number(move.skillID || 0) || this.normalizeMoveName(move.name))
          if (!selected[key]) {
            selected[key] = Object.assign({}, move, { levels: [] })
          }
          selected[key].levels.push(level)
        })
      }

      const moves = Object.keys(selected)
        .map(key => {
          const move = selected[key]
          const ranges = this.compactLevelRanges(move.levels || [])
          return Object.assign({}, move, {
            ranges,
            rangeText: this.formatLevelRanges(ranges)
          })
        })
        .sort((a, b) => {
          const levelCompare = a.level - b.level
          if (levelCompare !== 0) return levelCompare
          return a.id - b.id
        })
      this.moveCache[cacheKey] = moves
      return moves
    },
    moveTooltip (spawn) {
      const moves = this.possibleMoves(spawn)
      if (!moves.length) return 'No level-up moves found.'
      return moves.map(move => {
        const style = this.isSelfHarmingMove(move, spawn) ? ' style="color:#ff6b79;font-weight:700;"' : ''
        const range = move.rangeText ? ' <span style="color:#9fb0bf;">(' + this.escapeHtml(move.rangeText) + ')</span>' : ''
        return '<div' + style + '>Lv. ' + move.level + ': ' + this.escapeHtml(move.name) + range + '</div>'
      }).join('')
    },
    safeLevelRangeOptions (spawn) {
      if (!this.editMode) return []
      const min = this.clampNumber(spawn.minLVL || 1, 1, 100)
      const max = this.clampNumber(spawn.maxLVL || min, min, 100)
      const cacheKey = this.safeLevelRangeCacheKey(spawn, min, max)
      if (this.cacheHas(this.safeLevelRangeCache, cacheKey)) return this.safeLevelRangeCache[cacheKey]

      const unsafeLevels = this.selfHarmingUnsafeLevels(spawn)
      let firstUnsafe = 0
      let lastUnsafe = 0
      for (let level = min; level <= max; level++) {
        if (!unsafeLevels[level]) continue
        if (!firstUnsafe) firstUnsafe = level
        lastUnsafe = level
      }

      if (!firstUnsafe) {
        this.safeLevelRangeCache[cacheKey] = []
        return []
      }

      const options = []
      const lower = this.safeRangeBeforeUnsafe(unsafeLevels, min, firstUnsafe)
      const higher = this.safeRangeAfterUnsafe(unsafeLevels, max, lastUnsafe)
      if (lower) options.push(this.safeLevelRangeOption(lower.min, lower.max, 'lower'))
      if (higher && (!lower || lower.min !== higher.min || lower.max !== higher.max)) {
        options.push(this.safeLevelRangeOption(higher.min, higher.max, 'higher'))
      }
      this.safeLevelRangeCache[cacheKey] = options
      return options
    },
    safeLevelRangeCacheKey (spawn, min, max) {
      return [
        Number(spawn.dexID || 0),
        this.pokemonTypes(spawn).join(','),
        this.selfHarmingMoveSignature,
        min,
        max
      ].join('|')
    },
    selfHarmingUnsafeLevelCacheKey (spawn) {
      return [
        Number(spawn.dexID || 0),
        this.pokemonTypes(spawn).join(','),
        this.selfHarmingMoveSignature
      ].join('|')
    },
    selfHarmingUnsafeLevels (spawn) {
      const cacheKey = this.selfHarmingUnsafeLevelCacheKey(spawn)
      if (this.cacheHas(this.selfHarmingUnsafeLevelCache, cacheKey)) return this.selfHarmingUnsafeLevelCache[cacheKey]
      const unsafe = new Array(101).fill(false)
      if (!Number(spawn.dexID || 0) || !this.selfHarmingMoveSignature) {
        this.selfHarmingUnsafeLevelCache[cacheKey] = unsafe
        return unsafe
      }
      const fullRangeSpawn = Object.assign({}, spawn, { minLVL: 1, maxLVL: 100 })
      this.possibleMoves(fullRangeSpawn).forEach(move => {
        if (!this.isSelfHarmingMove(move, spawn)) return
        ;(move.levels || []).forEach(level => {
          if (level >= 1 && level <= 100) unsafe[level] = true
        })
      })
      this.selfHarmingUnsafeLevelCache[cacheKey] = unsafe
      return unsafe
    },
    safeRangeBeforeUnsafe (unsafeLevels, currentMin, firstUnsafe) {
      if (firstUnsafe > currentMin) {
        return { min: currentMin, max: firstUnsafe - 1 }
      }
      for (let level = firstUnsafe - 1; level >= 1; level--) {
        if (!unsafeLevels[level]) {
          return { min: level, max: level }
        }
      }
      return null
    },
    safeRangeAfterUnsafe (unsafeLevels, currentMax, lastUnsafe) {
      if (lastUnsafe < currentMax) {
        return { min: lastUnsafe + 1, max: currentMax }
      }
      for (let level = lastUnsafe + 1; level <= 100; level++) {
        if (!unsafeLevels[level]) {
          return { min: level, max: level }
        }
      }
      return null
    },
    safeLevelRangeOption (min, max, direction) {
      return {
        direction: direction + '-' + min + '-' + max,
        min,
        max,
        label: 'Use ' + (min === max ? 'Lv. ' + min : 'Lv. ' + min + '-' + max)
      }
    },
    spawnHasSelfHarmingMoves (spawn) {
      const min = this.clampNumber(spawn.minLVL || 1, 1, 100)
      const max = this.clampNumber(spawn.maxLVL || min, min, 100)
      return this.hasUnsafeSelfHarmingLevel(spawn, min, max)
    },
    isSafeLevelRange (spawn, min, max) {
      return !this.hasUnsafeSelfHarmingLevel(spawn, min, max)
    },
    hasUnsafeSelfHarmingLevel (spawn, min, max) {
      const unsafeLevels = this.selfHarmingUnsafeLevels(spawn)
      for (let level = min; level <= max; level++) {
        if (unsafeLevels[level]) return true
      }
      return false
    },
    applySafeLevelRange (spawn, option) {
      if (!option) return
      this.$set(spawn, 'minLVL', option.min)
      this.$set(spawn, 'maxLVL', option.max)
      this.$set(spawn, '_minLVLInput', String(option.min))
      this.$set(spawn, '_maxLVLInput', String(option.max))
    },
    compactLevelRanges (levels) {
      const sorted = (levels || []).slice().sort((a, b) => a - b)
      const ranges = []
      let start = null
      let previous = null
      sorted.forEach(level => {
        if (start === null) {
          start = level
          previous = level
          return
        }
        if (level === previous + 1) {
          previous = level
          return
        }
        ranges.push({ start, end: previous })
        start = level
        previous = level
      })
      if (start !== null) {
        ranges.push({ start, end: previous })
      }
      return ranges
    },
    formatLevelRanges (ranges) {
      return (ranges || []).map(range => range.start === range.end ? String(range.start) : range.start + '-' + range.end).join(', ')
    },
    normalizeMoveName (move) {
      return String(move || '').trim().toLowerCase()
    },
    pokemonTypes (spawn) {
      return [spawn.type1, spawn.type2]
        .map(type => String(type || '').trim().toLowerCase())
        .filter(type => type)
    },
    hasPokemonType (spawn, type) {
      return this.pokemonTypes(spawn).indexOf(String(type || '').toLowerCase()) !== -1
    },
    isSelfHarmingMove (move, spawn) {
      const skillID = Number(move && move.skillID ? move.skillID : move)
      if (!skillID || !this.selfHarmingMoveSet[skillID]) return false
      const normalized = this.normalizeMoveName(move && move.name ? move.name : '')
      if (normalized === 'curse') {
        return this.hasPokemonType(spawn, 'ghost')
      }
      if (normalized === 'sandstorm') {
        return !this.pokemonTypes(spawn).some(type => SANDSTORM_SAFE_TYPES.indexOf(type) !== -1)
      }
      return true
    },
    highlightMapSearch (value) {
      const search = String(this.mapSearch || '').trim()
      if (!search) return this.escapeHtml(value)
      const text = String(value || '')
      const lowerText = text.toLowerCase()
      const lowerSearch = search.toLowerCase()
      const index = lowerText.indexOf(lowerSearch)
      if (index === -1) return this.escapeHtml(text)
      return this.escapeHtml(text.slice(0, index)) +
        '<span class="spawn-highlight">' + this.escapeHtml(text.slice(index, index + search.length)) + '</span>' +
        this.escapeHtml(text.slice(index + search.length))
    },
    escapeHtml (value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    hasItem (spawn) {
      return Number(spawn.itemID || 0) > 0
    },
    itemName (spawn) {
      if (!this.hasItem(spawn)) return '\u2014'
      if (spawn.item) return spawn.item
      return this.itemNameByID[String(Number(spawn.itemID || 0))] || ('#' + spawn.itemID)
    },
    itemDropLabel (spawn) {
      return this.formatPercent(Number(spawn.itemRarity || 0) * 100)
    },
    rawRarity (spawn) {
      const rarity = Number(spawn.rarity || 0)
      return rarity.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')
    },
    rarityTier (spawn) {
      const rarity = Number(spawn.rarity || 0)
      const table = this.isWaterSpawn(spawn) ? WATER_RARITIES : LAND_RARITIES
      const match = table.find(entry => Math.abs(entry.value - rarity) < 0.0000001)
      return match ? match.tier : 'Custom'
    },
    rarityGroupKey (spawn) {
      const sortValue = this.raritySortValue(spawn)
      if (sortValue >= 1 && sortValue <= 3) return 'common'
      if (sortValue >= 4 && sortValue <= 6) return 'uncommon'
      if (sortValue >= 7 && sortValue <= 9.5) return 'rare'
      if (sortValue === 10) return 'unobtainable'
      return this.tierClassKey(this.rarityTier(spawn))
    },
    rarityGroupLabel (spawn) {
      const key = this.rarityGroupKey(spawn)
      if (key === 'common') return 'Common'
      if (key === 'uncommon') return 'Uncommon'
      if (key === 'rare') return 'Rare'
      if (key === 'unobtainable') return 'Unobtainable'
      return this.rarityTier(spawn)
    },
    isTierTen (tier) {
      const normalized = String(tier || '').trim().toLowerCase()
      return normalized === 't10' || normalized === 'tier 10' || normalized === 'tier10'
    },
    tierClassKey (tier) {
      return String(tier || 'custom').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'custom'
    },
    parseForms (spawn) {
      const seen = Object.create(null)
      return String(spawn.forms || '')
        .split(',')
        .map(value => Number(String(value).trim()))
        .filter(value => value > 0)
        .filter(value => {
          if (seen[value]) return false
          seen[value] = true
          return true
        })
        .sort((a, b) => a - b)
    },
    formEventName (speciesForms, formId) {
      const found = speciesForms.find(form => Number(form.id) === formId)
      return found && found.name ? found.name : 'Non-Event'
    },
    compactNumberRanges (ids) {
      const ranges = []
      let start = null
      let previous = null
      ids.forEach(id => {
        if (start === null) {
          start = id
          previous = id
          return
        }
        if (id === previous + 1) {
          previous = id
          return
        }
        ranges.push(start === previous ? String(start) : start + '-' + previous)
        start = id
        previous = id
      })
      if (start !== null) {
        ranges.push(start === previous ? String(start) : start + '-' + previous)
      }
      return ranges.join(',')
    },
    formLabels (spawn) {
      const formIds = this.parseForms(spawn)
      if (!formIds.length) return []
      const mappings = this.formMapping && this.formMapping.mappings ? this.formMapping.mappings : {}
      const speciesForms = mappings[Number(spawn.dexID)] || []
      const groups = []
      const indexes = Object.create(null)
      formIds.forEach(formId => {
        const name = this.formEventName(speciesForms, formId)
        if (indexes[name] === undefined) {
          indexes[name] = groups.length
          groups.push({ name: name, ids: [] })
        }
        groups[indexes[name]].ids.push(formId)
      })
      return groups.map(group => group.name + ' (' + this.compactNumberRanges(group.ids) + ')')
    },
    normalizeHour (value) {
      const hour = Number(value || 0)
      if (!Number.isFinite(hour)) return 0
      const normalized = hour % 24
      return normalized < 0 ? normalized + 24 : normalized
    },
    intervalsForRange (start, end) {
      const from = this.normalizeHour(start)
      const to = this.normalizeHour(end)
      if (from === 0 && to === 0) {
        return [[0, 24]]
      }
      if (from === to) {
        return []
      }
      if (from < to) {
        return [[from, to]]
      }
      return [[from, 24], [0, to]]
    },
    intervalsOverlap (a, b) {
      return a[0] < b[1] && b[0] < a[1]
    },
    activeTimeKeys (spawn) {
      const cacheKey = [
        spawn.noTimeSelected ? 1 : 0,
        this.normalizeHour(spawn.start),
        this.normalizeHour(spawn.end)
      ].join('|')
      if (this.cacheHas(this.activeTimeKeyCache, cacheKey)) return this.activeTimeKeyCache[cacheKey]
      if (spawn.noTimeSelected) {
        this.activeTimeKeyCache[cacheKey] = []
        return this.activeTimeKeyCache[cacheKey]
      }
      const start = this.normalizeHour(spawn.start)
      const end = this.normalizeHour(spawn.end)
      if (start === 0 && end === 0) {
        this.activeTimeKeyCache[cacheKey] = TIME_PERIODS.map(period => period.key)
        return this.activeTimeKeyCache[cacheKey]
      }
      const spawnIntervals = this.intervalsForRange(start, end)
      this.activeTimeKeyCache[cacheKey] = TIME_PERIODS
        .filter(period => {
          const periodIntervals = this.intervalsForRange(period.start, period.end)
          return spawnIntervals.some(spawnInterval => periodIntervals.some(periodInterval => this.intervalsOverlap(spawnInterval, periodInterval)))
        })
        .map(period => period.key)
      return this.activeTimeKeyCache[cacheKey]
    },
    isTimeActive (spawn, key) {
      return this.activeTimeKeys(spawn).indexOf(key) !== -1
    },
    isLastActiveTime (spawn, key) {
      const keys = this.activeTimeKeys(spawn)
      return keys.length === 1 && keys[0] === key
    },
    setSpawnTimeActive (spawn, key, active) {
      const selected = this.activeTimeKeys(spawn).reduce((result, activeKey) => {
        result[activeKey] = true
        return result
      }, Object.create(null))
      if (active) {
        selected[key] = true
      } else {
        delete selected[key]
      }
      const keys = TIME_PERIODS.map(period => period.key).filter(periodKey => selected[periodKey])
      if (!keys.length) {
        this.$set(spawn, 'noTimeSelected', true)
        return
      }
      const range = this.timeRangeForKeys(keys)
      this.$set(spawn, 'noTimeSelected', false)
      this.$set(spawn, 'start', range.start)
      this.$set(spawn, 'end', range.end)
    },
    timeRangeForKeys (keys) {
      const selected = keys.reduce((result, key) => {
        result[key] = true
        return result
      }, Object.create(null))
      const morning = !!selected.morning
      const day = !!selected.day
      const night = !!selected.night
      if (morning && day && night) return { start: 0, end: 0 }
      if (morning && day) return { start: 4, end: 20 }
      if (day && night) return { start: 10, end: 4 }
      if (morning && night) return { start: 20, end: 10 }
      if (morning) return { start: 4, end: 10 }
      if (day) return { start: 10, end: 20 }
      return { start: 20, end: 4 }
    },
    emptySpawnProbabilityTotalsByAreaPeriod () {
      const totals = {
        land: Object.create(null),
        water: Object.create(null)
      }
      TIME_PERIODS.forEach(period => {
        totals.land[period.key] = 0
        totals.water[period.key] = 0
      })
      return totals
    },
    buildSpawnProbabilityTotalsByAreaPeriod (rows) {
      const totals = this.emptySpawnProbabilityTotalsByAreaPeriod()
      ;(rows || []).forEach(spawn => {
        if (!this.membershipActive && spawn.memberOnly) return
        const rarity = Number(spawn.rarity || 0)
        if (rarity <= 0) return
        const water = this.isWaterSpawn(spawn)
        if (water && !this.hasSurfEncounter(spawn)) return
        const area = water ? 'water' : 'land'
        this.activeTimeKeys(spawn).forEach(key => {
          if (totals[area][key] !== undefined) {
            totals[area][key] += rarity
          }
        })
      })
      return totals
    },
    emptyFishingProbabilityTotalsByRodPeriod () {
      return FISHING_RODS.reduce((result, rod) => {
        result[rod.key] = Object.create(null)
        TIME_PERIODS.forEach(period => {
          result[rod.key][period.key] = 0
        })
        return result
      }, Object.create(null))
    },
    buildFishingProbabilityTotalsByRodPeriod (rows) {
      const totals = this.emptyFishingProbabilityTotalsByRodPeriod()
      ;(rows || []).forEach(spawn => {
        if (!this.isWaterSpawn(spawn) || !this.isFishable(spawn)) return
        if (!this.membershipActive && spawn.memberOnly) return
        const rarity = Number(spawn.rarity || 0)
        if (rarity <= 0) return
        const activeKeys = this.activeTimeKeys(spawn)
        FISHING_RODS.forEach(rod => {
          if (Number(spawn.required_rod || 0) > rod.requiredRod) return
          activeKeys.forEach(key => {
            if (totals[rod.key][key] !== undefined) {
              totals[rod.key][key] += rarity
            }
          })
        })
      })
      return totals
    },
    probabilityPoolForRows (spawn, key, rows) {
      const spawnIsWater = this.isWaterSpawn(spawn)
      return (rows || []).filter(row => {
        if (this.isWaterSpawn(row) !== spawnIsWater) return false
        if (!this.membershipActive && row.memberOnly) return false
        if (!this.isTimeActive(row, key)) return false
        if (Number(row.rarity || 0) <= 0) return false
        if (!spawnIsWater) return true
        return this.hasSurfEncounter(row)
      })
    },
    spawnProbability (spawn, key) {
      return this.spawnProbabilityWithTotals(spawn, key, this.spawnProbabilityTotalsByAreaPeriod)
    },
    spawnProbabilityWithTotals (spawn, key, totalsByAreaPeriod) {
      if (!this.isTimeActive(spawn, key)) return null
      if (!this.membershipActive && spawn.memberOnly) return 0
      const water = this.isWaterSpawn(spawn)
      if (water && !this.hasSurfEncounter(spawn)) return null
      const area = water ? 'water' : 'land'
      const total = Number(((totalsByAreaPeriod || {})[area] || {})[key] || 0)
      if (total <= 0) return null
      return Number(spawn.rarity || 0) / total * 100
    },
    spawnProbabilityForRows (spawn, key, rows) {
      if (!this.isTimeActive(spawn, key)) return null
      if (!this.membershipActive && spawn.memberOnly) return 0
      if (this.isWaterSpawn(spawn) && !this.hasSurfEncounter(spawn)) return null
      const pool = this.probabilityPoolForRows(spawn, key, rows)
      const total = pool.reduce((sum, row) => sum + Number(row.rarity || 0), 0)
      if (total <= 0) return null
      return Number(spawn.rarity || 0) / total * 100
    },
    addSpawnPreviewProbabilityLabel (pokemon, key) {
      const spawn = this.createSpawnForPokemon(pokemon, this.addSpawnModal.area, true)
      const value = this.spawnProbabilityForRows(spawn, key, this.addSpawnPreviewRows)
      return value === null ? '-' : this.formatPercent(value)
    },
    addSpawnPreviewProbabilityDisplayLabel (pokemon, key) {
      const label = this.addSpawnPreviewProbabilityLabel(pokemon, key)
      return this.shouldCensorProbabilityLabel(label) ? this.censoredProbabilityLabel() : label
    },
    addSpawnExistingProbabilityLabel (spawn, key) {
      if (this.isAddSpawnExistingRemovalPending(spawn)) return '-'
      const effective = this.addSpawnExistingEffectiveSpawn(spawn)
      if (!this.isTimeActive(effective, key)) return '-'
      const value = this.spawnProbabilityForRows(effective, key, this.addSpawnPreviewRows)
      return value === null ? '-' : this.formatPercent(value)
    },
    addSpawnExistingProbabilityDisplayLabel (spawn, key) {
      const label = this.addSpawnExistingProbabilityLabel(spawn, key)
      return this.shouldCensorProbabilityLabel(label) ? this.censoredProbabilityLabel() : label
    },
    fishingProbability (spawn, rod, key) {
      return this.fishingProbabilityWithTotals(spawn, rod, key, this.fishingProbabilityTotalsByRodPeriod)
    },
    fishingProbabilityWithTotals (spawn, rod, key, totalsByRodPeriod) {
      if (!this.isWaterSpawn(spawn) || !this.isFishable(spawn)) return null
      if (Number(spawn.required_rod || 0) > rod.requiredRod) return null
      if (!this.isTimeActive(spawn, key)) return 0
      if (!this.membershipActive && spawn.memberOnly) return 0
      const total = Number((((totalsByRodPeriod || {})[rod.key] || {})[key]) || 0)
      if (total <= 0) return 0
      return Number(spawn.rarity || 0) / total * 100
    },
    fishingChanceRows (spawn) {
      return this.fishingChanceRowsWithTotals(spawn, this.fishingProbabilityTotalsByRodPeriod)
    },
    fishingChanceRowsWithTotals (spawn, totalsByRodPeriod) {
      if (!this.isWaterSpawn(spawn) || !this.isFishable(spawn)) return []
      return FISHING_RODS
        .map(rod => {
          const chances = TIME_PERIODS.map(period => {
            const value = this.fishingProbabilityWithTotals(spawn, rod, period.key, totalsByRodPeriod)
            return {
              key: period.key,
              label: period.label.charAt(0),
              raw: value,
              value: value === null ? '0%' : this.formatPercent(value)
            }
          })
          const groupedChances = this.groupFishingChances(chances)
          return {
            key: rod.key,
            label: rod.label,
            icon: rod.icon,
            chances: groupedChances,
            hasChance: groupedChances.length > 0
          }
        })
        .filter(row => row.hasChance)
    },
    groupFishingChances (chances) {
      const groups = Object.create(null)
      ;(chances || []).forEach((chance, index) => {
        if (Number(chance.raw || 0) <= 0) return
        const censored = this.shouldCensorProbabilityLabel(chance.value)
        const key = censored ? 'censored-' + chance.value : chance.value
        if (!groups[key]) {
          groups[key] = {
            key: key,
            firstIndex: index,
            labels: [],
            value: censored ? this.censoredProbabilityLabel() : chance.value,
            censored
          }
        }
        groups[key].labels.push(chance.label)
      })
      return Object.values(groups)
        .sort((a, b) => a.firstIndex - b.firstIndex)
        .map(group => ({
          key: group.key + '-' + group.labels.join('-'),
          label: group.labels.join('/'),
          value: group.value,
          censored: group.censored
        }))
    },
    probabilityLabel (spawn, key) {
      return this.probabilityLabelWithTotals(spawn, key, this.spawnProbabilityTotalsByAreaPeriod)
    },
    probabilityLabelWithTotals (spawn, key, totalsByAreaPeriod) {
      const value = this.spawnProbabilityWithTotals(spawn, key, totalsByAreaPeriod)
      if (value === null) {
        return '-'
      }
      return this.formatPercent(value)
    },
    formatPercent (value) {
      const numeric = Number(value)
      if (!Number.isFinite(numeric)) return '-'
      if (numeric === 0) return '0%'
      if (numeric < 0.01) return numeric.toFixed(4) + '%'
      if (numeric < 1) return numeric.toFixed(3) + '%'
      if (numeric < 10) return numeric.toFixed(2) + '%'
      return numeric.toFixed(1) + '%'
    }
  }
}
</script>
